import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { statsAPI, questionAPI, tryoutAdminAPI, packageAdminAPI, promoAPI, zoomClassAPI, landingContentAPI, usersAPI, mapelTimeAPI, questionImportExportAPI, subjectAPI, categoryAdminAPI, subscriptionTierAPI, eventTryoutAPI, ebookAPI } from '@/lib/api';
import { formatPrice } from '@/lib/utils';
import { sanitizeHTML } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor, QuestionPreview, MiniRichTextEditor } from '@/components/RichTextEditor';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import LandingSettingsTab from '@/components/Admin/LandingSettingsTab';
import AnalyticsDashboard from '@/components/Admin/AnalyticsDashboard';
import BatchImportTab from '@/components/Admin/BatchImportTab';
import EventTryOutTab from '@/components/Admin/EventTryOutTab';
import MateriTab from '@/components/Admin/MateriTab';
import EmailListTab from '@/components/Admin/EmailListTab';
import AdminSettingsTab from '@/components/Admin/AdminSettingsTab';
import ExamResultsTab from '@/components/Admin/ExamResultsTab';
import ChangePasswordTab from '@/components/Admin/ChangePasswordTab';
import PurchaseManagementTab from '@/components/Admin/PurchaseManagementTab';
import SubjectConfigDialog from '@/components/Admin/SubjectConfigDialog';
import EbookDialog from '@/components/Admin/EbookDialog';
import TestimonialsTab from '@/components/Admin/TestimonialsTab';
import ProgramImagesTab from '@/components/Admin/ProgramImagesTab';

// Helper function to strip HTML tags for preview display
const stripHtmlForPreview = (html) => {
  if (!html) return '';
  // Remove HTML tags and decode common entities
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim()
    .substring(0, 150);
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  BookOpen, Users, FileText, Package, Trophy, LogOut, DollarSign,
  Plus, Pencil, Trash2, Search, LayoutDashboard, X, Check, Download,
  Upload, File, Clock, Eye, Home, Video, Gift, MonitorPlay, Newspaper,
  Calendar, Link as LinkIcon, Percent, Tag, AlertTriangle, CheckCircle, Crown,
  Layers, List, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Sparkles,
  BarChart3, FileUp, Menu, Mail, UserCog, KeyRound, ClipboardList, ShoppingCart, Settings, Loader2,
  Edit, Star, Quote, Scissors, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

// Program options - sesuai permintaan user
const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'BUMN',
  'TKA',
  'SMA Favorit'
];

// Alias for PackageDialog
const PROGRAM_OPTIONS = PROGRAMS;

// Semua Mata Pelajaran yang tersedia
const ALL_MAPEL = [
  'Tes Wawasan Kebangsaan',
  'Tes Intelegensi Umum',
  'Tes Karakteristik Pribadi',
  'Penalaran Umum',
  'Pengetahuan dan Pemahaman Umum',
  'Pemahaman Bacaan dan Menulis',
  'Pengetahuan Kuantitatif',
  'Literasi Bahasa Indonesia',
  'Literasi Bahasa Inggris',
  'Penalaran Matematika',
  'Matematika',
  'Bahasa Inggris',
  'Bahasa Indonesia',
  'Geografi',
  'Ekonomi',
  'Sejarah',
  'Sosiologi',
  'Psikotes',
  'Fisika',
  'Kimia',
  'Biologi'
];

// Mata Pelajaran (Subject) options per Program
const MAPEL_OPTIONS = {
  'UTBK-SNBT': [
    'Penalaran Umum',
    'Pengetahuan dan Pemahaman Umum',
    'Pemahaman Bacaan dan Menulis',
    'Pengetahuan Kuantitatif',
    'Literasi Bahasa Indonesia',
    'Literasi Bahasa Inggris',
    'Penalaran Matematika'
  ],
  'CPNS': [
    'Tes Wawasan Kebangsaan',
    'Tes Intelegensi Umum',
    'Tes Karakteristik Pribadi'
  ],
  'TNI': [
    'Tes Wawasan Kebangsaan',
    'Tes Intelegensi Umum',
    'Tes Karakteristik Pribadi',
    'Psikotes'
  ],
  'POLRI': [
    'Tes Wawasan Kebangsaan',
    'Tes Intelegensi Umum',
    'Tes Karakteristik Pribadi',
    'Psikotes'
  ],
  'SEKOLAH KEDINASAN': [
    'Tes Wawasan Kebangsaan',
    'Tes Intelegensi Umum',
    'Tes Karakteristik Pribadi'
  ],
  'BUMN': [
    'Tes Wawasan Kebangsaan',
    'Tes Intelegensi Umum',
    'Tes Karakteristik Pribadi',
    'Bahasa Inggris'
  ],
  'TKA': [
    'Matematika',
    'Fisika',
    'Kimia',
    'Biologi',
    'Ekonomi',
    'Geografi',
    'Sejarah',
    'Sosiologi'
  ],
  'SMA Favorit': [
    'Matematika',
    'Bahasa Indonesia',
    'Bahasa Inggris',
    'Fisika',
    'Kimia',
    'Biologi'
  ]
};

// Generate Kategori options (1-10) for each mapel
const generateKategori = (mapel) => {
  if (!mapel) return [];
  const prefix = mapel.replace(/\s+/g, '-').toUpperCase();
  return Array.from({ length: 20 }, (_, i) => `${prefix}-${i + 1}`);
};

// Time limit options
const TIME_LIMITS = [
  { value: '0', label: 'Tanpa Batas Waktu' },
  { value: '30', label: '30 detik' },
  { value: '60', label: '60 detik' },
  { value: '90', label: '90 detik' },
  { value: '120', label: '120 detik' },
  { value: '180', label: '180 detik' },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Questions State - with server-side pagination
  const [questions, setQuestions] = useState([]);
  const [questionSearch, setQuestionSearch] = useState('');
  const [questionFilter, setQuestionFilter] = useState({ program: '', mapel: '', kategori: '', access_type: '' });
  const [questionDialog, setQuestionDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteQuestionDialog, setDeleteQuestionDialog] = useState({ open: false, id: null });
  const [questionPagination, setQuestionPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
    hasMore: false
  });
  const [questionStats, setQuestionStats] = useState({ total: 0, by_program: {} });
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // TryOuts State
  const [tryouts, setTryouts] = useState([]);
  const [tryoutDialog, setTryoutDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteTryoutDialog, setDeleteTryoutDialog] = useState({ open: false, id: null });
  const [questionSelectDialog, setQuestionSelectDialog] = useState({ open: false, tryoutId: null, selectedIds: [] });
  const [subjectConfigDialog, setSubjectConfigDialog] = useState({ open: false, tryout: null });
  const [trimQuestionDialog, setTrimQuestionDialog] = useState({ open: false, tryout: null, targetCount: 0 });

  // Packages State
  const [packages, setPackages] = useState([]);
  const [packageDialog, setPackageDialog] = useState({ open: false, mode: 'create', data: null });
  const [deletePackageDialog, setDeletePackageDialog] = useState({ open: false, id: null });
  const [tryoutSelectDialog, setTryoutSelectDialog] = useState({ open: false, packageId: null, selectedIds: [] });

  // Promos State
  const [promos, setPromos] = useState([]);
  const [promoDialog, setPromoDialog] = useState({ open: false, mode: 'create', data: null });
  const [deletePromoDialog, setDeletePromoDialog] = useState({ open: false, id: null });

  // Zoom Classes State
  const [zoomClasses, setZoomClasses] = useState([]);
  const [zoomDialog, setZoomDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteZoomDialog, setDeleteZoomDialog] = useState({ open: false, id: null });

  // Landing Content State
  const [landingContent, setLandingContent] = useState([]);
  const [landingDialog, setLandingDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteLandingDialog, setDeleteLandingDialog] = useState({ open: false, id: null });

  // Users State
  const [users, setUsers] = useState([]);
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, id: null });
  const [userDialog, setUserDialog] = useState({ open: false, mode: 'edit', data: null });

  // Question Bulk Delete State
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false });

  // Subjects (Mata Pelajaran) State
  const [subjects, setSubjects] = useState([]);
  const [subjectDialog, setSubjectDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteSubjectDialog, setDeleteSubjectDialog] = useState({ open: false, id: null });
  const [subjectFilter, setSubjectFilter] = useState('');

  // Categories (Kategori) State
  const [categories, setCategories] = useState([]);
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteCategoryDialog, setDeleteCategoryDialog] = useState({ open: false, id: null });
  const [categoryFilter, setCategoryFilter] = useState({ program: '', subject: '' });

  // eBooks State
  const [ebooks, setEbooks] = useState([]);
  const [ebookDialog, setEbookDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteEbookDialog, setDeleteEbookDialog] = useState({ open: false, id: null });

  // Auto-generate Try Out State
  const [autoGenDialog, setAutoGenDialog] = useState({ open: false });
  
  // Generate Paket Lengkap State
  const [paketGenDialog, setPaketGenDialog] = useState({ open: false });

  // Import/Export State
  const [importDialog, setImportDialog] = useState({ open: false });
  const fileInputRef = useRef(null);

  // Mapel Time Settings State
  const [mapelTimeSettings, setMapelTimeSettings] = useState([]);

  // Subscription Tiers State
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [tierDialog, setTierDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteTierDialog, setDeleteTierDialog] = useState({ open: false, id: null });

  // Event Try Outs State
  const [eventTryouts, setEventTryouts] = useState([]);
  const [eventDialog, setEventDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteEventDialog, setDeleteEventDialog] = useState({ open: false, id: null });

  // Tryout Preview State (for previewing all questions in a tryout)
  const [tryoutPreviewDialog, setTryoutPreviewDialog] = useState({ open: false, tryout: null, questions: [], loading: false });

  useEffect(() => {
    loadData();
  }, []);

  // Load questions with server-side pagination
  const loadQuestions = useCallback(async (page = 1, filters = questionFilter, search = questionSearch) => {
    try {
      setLoadingQuestions(true);
      const params = {
        page,
        limit: questionPagination.limit,
        ...(filters.program && { program: filters.program }),
        ...(filters.mapel && { mapel: filters.mapel }),
        ...(filters.kategori && { category: filters.kategori }),
        ...(filters.access_type && { access_type: filters.access_type }),
        ...(search && { search })
      };
      
      const res = await questionAPI.getAll(params);
      const data = res.data;
      
      setQuestions(data.questions || []);
      setQuestionPagination({
        page: data.page,
        limit: data.limit,
        total: data.total,
        totalPages: data.total_pages,
        hasMore: data.has_more
      });
    } catch (err) {
      console.error('Failed to load questions:', err);
      toast.error('Gagal memuat soal');
    } finally {
      setLoadingQuestions(false);
    }
  }, [questionFilter, questionSearch, questionPagination.limit]);

  // Reload questions when filter/search changes
  useEffect(() => {
    if (!loading) {
      const debounceTimer = setTimeout(() => {
        loadQuestions(1, questionFilter, questionSearch);
      }, 300); // Debounce search
      return () => clearTimeout(debounceTimer);
    }
  }, [questionFilter, questionSearch, loading]);

  const loadData = async () => {
    try {
      const [statsRes, questionsRes, tryoutsRes, packagesRes, questionStatsRes] = await Promise.all([
        statsAPI.get(),
        questionAPI.getAll({ page: 1, limit: 100 }),
        tryoutAdminAPI.getAll(),
        packageAdminAPI.getAll(),
        questionAPI.getStats()
      ]);
      setStats(statsRes.data);
      
      // Handle paginated response
      const questionsData = questionsRes.data;
      setQuestions(questionsData.questions || []);
      setQuestionPagination({
        page: questionsData.page || 1,
        limit: questionsData.limit || 100,
        total: questionsData.total || 0,
        totalPages: questionsData.total_pages || 1,
        hasMore: questionsData.has_more || false
      });
      
      // Set question stats
      setQuestionStats(questionStatsRes.data);
      
      setTryouts(tryoutsRes.data);
      setPackages(packagesRes.data);
      
      // Load additional data
      try {
        const [promosRes, zoomRes, landingRes, usersRes, mapelTimeRes, subjectsRes, categoriesRes, tiersRes, ebooksRes] = await Promise.all([
          promoAPI.getAll(),
          zoomClassAPI.getAll(),
          landingContentAPI.getAll(),
          usersAPI.getAll(),
          mapelTimeAPI.getAll(),
          subjectAPI.getAll(),
          categoryAdminAPI.getAll(),
          subscriptionTierAPI.getAll(),
          ebookAPI.getAll()
        ]);
        setPromos(promosRes.data);
        setZoomClasses(zoomRes.data);
        setLandingContent(landingRes.data);
        setUsers(usersRes.data);
        setMapelTimeSettings(mapelTimeRes.data);
        setSubjects(subjectsRes.data || []);
        setCategories(categoriesRes.data || []);
        setSubscriptionTiers(tiersRes.data || []);
        setEbooks(ebooksRes.data || []);
      } catch (err) {
        console.log('Additional data not loaded:', err);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Question Handlers
  const handleSaveQuestion = async (formData) => {
    try {
      if (questionDialog.mode === 'create') {
        await questionAPI.create(formData);
      } else {
        await questionAPI.update(questionDialog.data.id, formData);
      }
      setQuestionDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save question:', err);
    }
  };

  const handleDeleteQuestion = async () => {
    try {
      await questionAPI.delete(deleteQuestionDialog.id);
      setDeleteQuestionDialog({ open: false, id: null });
      loadData();
    } catch (err) {
      console.error('Failed to delete question:', err);
    }
  };

  // TryOut Handlers
  const handleSaveTryout = async (formData) => {
    try {
      if (tryoutDialog.mode === 'create') {
        await tryoutAdminAPI.create(formData);
      } else {
        await tryoutAdminAPI.update(tryoutDialog.data.id, formData);
      }
      setTryoutDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save tryout:', err);
    }
  };

  const handleDeleteTryout = async () => {
    try {
      await tryoutAdminAPI.delete(deleteTryoutDialog.id);
      setDeleteTryoutDialog({ open: false, id: null });
      loadData();
    } catch (err) {
      console.error('Failed to delete tryout:', err);
    }
  };

  // Preview all questions in a tryout
  const handlePreviewTryoutQuestions = async (tryout) => {
    setTryoutPreviewDialog({ open: true, tryout, questions: [], loading: true });
    try {
      const res = await tryoutAdminAPI.previewQuestions(tryout.id);
      setTryoutPreviewDialog(prev => ({
        ...prev,
        questions: res.data.questions || [],
        loading: false
      }));
    } catch (err) {
      console.error('Failed to load questions:', err);
      toast.error('Gagal memuat soal: ' + (err.response?.data?.detail || err.message));
      setTryoutPreviewDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSaveQuestionSelection = async () => {
    try {
      await tryoutAdminAPI.update(questionSelectDialog.tryoutId, {
        question_ids: questionSelectDialog.selectedIds
      });
      setQuestionSelectDialog({ open: false, tryoutId: null, selectedIds: [] });
      loadData();
    } catch (err) {
      console.error('Failed to update questions:', err);
    }
  };

  // Package Handlers
  const handleSavePackage = async (formData) => {
    try {
      if (packageDialog.mode === 'create') {
        await packageAdminAPI.create(formData);
      } else {
        await packageAdminAPI.update(packageDialog.data.id, formData);
      }
      setPackageDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save package:', err);
    }
  };

  const handleDeletePackage = async () => {
    try {
      await packageAdminAPI.delete(deletePackageDialog.id);
      setDeletePackageDialog({ open: false, id: null });
      loadData();
    } catch (err) {
      console.error('Failed to delete package:', err);
    }
  };

  const handleSaveTryoutSelection = async () => {
    try {
      await packageAdminAPI.update(tryoutSelectDialog.packageId, {
        tryout_ids: tryoutSelectDialog.selectedIds
      });
      setTryoutSelectDialog({ open: false, packageId: null, selectedIds: [] });
      loadData();
    } catch (err) {
      console.error('Failed to update tryouts:', err);
    }
  };

  // Promo Handlers
  const handleSavePromo = async (formData) => {
    try {
      if (promoDialog.mode === 'create') {
        await promoAPI.create(formData);
        toast.success('Promo berhasil ditambahkan');
      } else {
        await promoAPI.update(promoDialog.data.id, formData);
        toast.success('Promo berhasil diperbarui');
      }
      setPromoDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save promo:', err);
      toast.error('Gagal menyimpan promo');
    }
  };

  const handleDeletePromo = async () => {
    try {
      await promoAPI.delete(deletePromoDialog.id);
      setDeletePromoDialog({ open: false, id: null });
      toast.success('Promo berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete promo:', err);
      toast.error('Gagal menghapus promo');
    }
  };

  // Zoom Class Handlers
  const handleSaveZoomClass = async (formData) => {
    try {
      if (zoomDialog.mode === 'create') {
        await zoomClassAPI.create(formData);
        toast.success('Kelas Zoom berhasil ditambahkan');
      } else {
        await zoomClassAPI.update(zoomDialog.data.id, formData);
        toast.success('Kelas Zoom berhasil diperbarui');
      }
      setZoomDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save zoom class:', err);
      toast.error('Gagal menyimpan kelas zoom');
    }
  };

  const handleDeleteZoomClass = async () => {
    try {
      await zoomClassAPI.delete(deleteZoomDialog.id);
      setDeleteZoomDialog({ open: false, id: null });
      toast.success('Kelas Zoom berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete zoom class:', err);
      toast.error('Gagal menghapus kelas zoom');
    }
  };

  // Landing Content Handlers
  const handleSaveLandingContent = async (formData) => {
    try {
      if (landingDialog.mode === 'create') {
        await landingContentAPI.create(formData);
        toast.success('Konten berhasil ditambahkan');
      } else {
        await landingContentAPI.update(landingDialog.data.id, formData);
        toast.success('Konten berhasil diperbarui');
      }
      setLandingDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save landing content:', err);
      toast.error('Gagal menyimpan konten');
    }
  };

  const handleDeleteLandingContent = async () => {
    try {
      await landingContentAPI.delete(deleteLandingDialog.id);
      setDeleteLandingDialog({ open: false, id: null });
      toast.success('Konten berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete landing content:', err);
      toast.error('Gagal menghapus konten');
    }
  };

  // User Handlers
  const handleDeleteUser = async () => {
    try {
      await usersAPI.delete(deleteUserDialog.id);
      setDeleteUserDialog({ open: false, id: null });
      toast.success('User berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete user:', err);
      toast.error('Gagal menghapus user');
    }
  };

  const handleSaveUser = async (formData) => {
    try {
      await usersAPI.update(userDialog.data.id, formData);
      setUserDialog({ open: false, mode: 'edit', data: null });
      toast.success('User berhasil diperbarui');
      loadData();
    } catch (err) {
      console.error('Failed to save user:', err);
      toast.error(err.response?.data?.detail || 'Gagal menyimpan user');
    }
  };

  // Bulk Delete Questions Handler
  const handleBulkDeleteQuestions = async () => {
    try {
      if (selectedQuestionIds.length === 0) {
        toast.error('Pilih soal yang ingin dihapus');
        return;
      }
      const res = await questionAPI.bulkDelete(selectedQuestionIds);
      setBulkDeleteDialog({ open: false });
      setSelectedQuestionIds([]);
      toast.success(res.data.message || `${selectedQuestionIds.length} soal berhasil dihapus`);
      loadData();
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      toast.error('Gagal menghapus soal');
    }
  };

  const toggleQuestionSelection = (id) => {
    setSelectedQuestionIds(prev => 
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  const toggleAllQuestions = () => {
    if (selectedQuestionIds.length === filteredQuestions.length) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(filteredQuestions.map(q => q.id));
    }
  };

  // Subject (Mata Pelajaran) Handlers
  const handleSaveSubject = async (formData) => {
    try {
      if (subjectDialog.mode === 'create') {
        await subjectAPI.create(formData);
        toast.success('Mata Pelajaran berhasil ditambahkan');
      } else {
        await subjectAPI.update(subjectDialog.data.id, formData);
        toast.success('Mata Pelajaran berhasil diupdate');
      }
      setSubjectDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save subject:', err);
      toast.error('Gagal menyimpan mata pelajaran');
    }
  };

  const handleDeleteSubject = async () => {
    try {
      await subjectAPI.delete(deleteSubjectDialog.id);
      setDeleteSubjectDialog({ open: false, id: null });
      toast.success('Mata Pelajaran berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete subject:', err);
      toast.error('Gagal menghapus mata pelajaran');
    }
  };

  // Category (Kategori) Handlers
  const handleSaveCategory = async (formData) => {
    try {
      if (categoryDialog.mode === 'create') {
        await categoryAdminAPI.create(formData);
        toast.success('Kategori berhasil ditambahkan');
      } else {
        await categoryAdminAPI.update(categoryDialog.data.id, formData);
        toast.success('Kategori berhasil diupdate');
      }
      setCategoryDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save category:', err);
      toast.error('Gagal menyimpan kategori');
    }
  };

  const handleDeleteCategory = async () => {
    try {
      await categoryAdminAPI.delete(deleteCategoryDialog.id);
      setDeleteCategoryDialog({ open: false, id: null });
      toast.success('Kategori berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete category:', err);
      toast.error('Gagal menghapus kategori');
    }
  };

  // eBook Handlers
  const handleSaveEbook = async (formData) => {
    try {
      if (ebookDialog.mode === 'edit') {
        await ebookAPI.update(ebookDialog.data.id, formData);
        toast.success('eBook berhasil diperbarui');
      } else {
        await ebookAPI.create(formData);
        toast.success('eBook berhasil ditambahkan');
      }
      setEbookDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save ebook:', err);
      toast.error('Gagal menyimpan eBook');
    }
  };

  const handleDeleteEbook = async () => {
    try {
      await ebookAPI.delete(deleteEbookDialog.id);
      setDeleteEbookDialog({ open: false, id: null });
      toast.success('eBook berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete ebook:', err);
      toast.error('Gagal menghapus eBook');
    }
  };

  // Subscription Tier Handlers
  const handleSaveTier = async (formData) => {
    try {
      if (tierDialog.mode === 'edit') {
        await subscriptionTierAPI.update(tierDialog.data.id, formData);
        toast.success('Subscription tier berhasil diperbarui');
      } else {
        await subscriptionTierAPI.create(formData);
        toast.success('Subscription tier berhasil ditambahkan');
      }
      setTierDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (err) {
      console.error('Failed to save tier:', err);
      toast.error('Gagal menyimpan subscription tier');
    }
  };

  const handleDeleteTier = async () => {
    try {
      await subscriptionTierAPI.delete(deleteTierDialog.id);
      setDeleteTierDialog({ open: false, id: null });
      toast.success('Subscription tier berhasil dihapus');
      loadData();
    } catch (err) {
      console.error('Failed to delete tier:', err);
      toast.error('Gagal menghapus subscription tier');
    }
  };

  // Import/Export Handlers
  const handleDownloadTemplate = async () => {
    try {
      const res = await questionImportExportAPI.getTemplate();
      const template = res.data.template;
      
      // Create CSV content with multiple example rows
      const headers = template.columns.join(',');
      const exampleRows = template.example_rows 
        ? template.example_rows.map(row => 
            template.columns.map(col => `"${(row[col] || '').toString().replace(/"/g, '""')}"`).join(',')
          ).join('\n')
        : template.columns.map(col => `"${template.example_row?.[col] || ''}"`).join(',');
      
      const instructions = template.instructions.map(i => `# ${i}`).join('\n');
      
      const csvContent = `${instructions}\n\n${headers}\n${exampleRows}`;
      
      // Download with BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_soal_mytentor.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template berhasil diunduh');
    } catch (err) {
      console.error('Failed to download template:', err);
      toast.error('Gagal mengunduh template');
    }
  };

  const handleExportQuestions = async () => {
    try {
      const res = await questionImportExportAPI.exportQuestions(questionFilter.program || null);
      const questions = res.data.questions;
      
      if (questions.length === 0) {
        toast.error('Tidak ada soal untuk diekspor');
        return;
      }
      
      // Create CSV
      const headers = Object.keys(questions[0]).join(',');
      const rows = questions.map(q => 
        Object.values(q).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      const csvContent = `${headers}\n${rows}`;
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `soal_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${questions.length} soal berhasil diekspor`);
    } catch (err) {
      console.error('Failed to export:', err);
      toast.error('Gagal mengekspor soal');
    }
  };

  // Handle trim question count for tryout
  const handleTrimTryoutQuestions = async (tryoutId, targetCount) => {
    try {
      const res = await tryoutAdminAPI.trimToCount(tryoutId, targetCount);
      if (res.data.success) {
        toast.success(`Berhasil: ${res.data.original_count} → ${res.data.new_count} soal (hapus ${res.data.removed})`);
        loadData();
        setTrimQuestionDialog({ open: false, tryout: null, targetCount: 0 });
      }
    } catch (err) {
      console.error('Failed to trim questions:', err);
      toast.error('Gagal memperbaiki jumlah soal');
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => !line.startsWith('#') && line.trim());
      
      if (lines.length < 2) {
        toast.error('File CSV tidak valid');
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
      const questions = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].match(/("([^"]|"")*"|[^,]*)/g)?.map(v => v.replace(/^"|"$/g, '').replace(/""/g, '"').trim()) || [];
        if (values.length < headers.length) continue;
        
        const q = {};
        headers.forEach((h, idx) => {
          q[h] = values[idx] || '';
        });
        questions.push(q);
      }
      
      if (questions.length === 0) {
        toast.error('Tidak ada data valid dalam file');
        return;
      }
      
      const res = await questionImportExportAPI.importQuestions(questions);
      toast.success(`${res.data.imported} soal berhasil diimpor`);
      if (res.data.errors.length > 0) {
        toast.error(`${res.data.errors.length} soal gagal diimpor`);
      }
      loadData();
    } catch (err) {
      console.error('Failed to import:', err);
      toast.error('Gagal mengimpor soal');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // With server-side pagination, questions array is already filtered
  // Just use the questions directly
  const filteredQuestions = questions;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Mobile Header with hamburger menu */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4 py-3">
        <button
          onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          className="p-2 rounded-lg hover:bg-slate-100"
          data-testid="mobile-menu-toggle"
        >
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        <span className="font-semibold text-slate-900">Admin Panel</span>
        <button
          onClick={() => { logout(); navigate('/'); }}
          className="p-2 rounded-lg hover:bg-slate-100 text-red-500"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0`}>
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
              <img src="/logo-mytentor.png" alt="MyTentor Logo" className="w-10 h-10 object-contain" />
              <div>
                <span className="text-lg font-bold text-slate-900">Bimbel My Tentor</span>
                <p className="text-xs text-slate-500">Admin Panel</p>
              </div>
            </Link>
            <button
              onClick={() => setMobileSidebarOpen(false)}
              className="md:hidden p-1 rounded hover:bg-slate-100"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
        
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <button
            onClick={() => { setActiveTab('overview'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'overview' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-overview"
          >
            <LayoutDashboard className="w-5 h-5" />
            Overview
          </button>
          <button
            onClick={() => { setActiveTab('questions'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'questions' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-questions"
          >
            <FileText className="w-5 h-5" />
            Manajemen Soal
          </button>
          <button
            onClick={() => { setActiveTab('subjects'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'subjects' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-subjects"
          >
            <BookOpen className="w-5 h-5" />
            Mata Pelajaran
          </button>
          <button
            onClick={() => { setActiveTab('categories'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'categories' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-categories"
          >
            <Tag className="w-5 h-5" />
            Kategori
          </button>
          <button
            onClick={() => { setActiveTab('paket-tryout'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'paket-tryout' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-paket-tryout"
          >
            <Package className="w-5 h-5" />
            Paket Try Out
          </button>
          <button
            onClick={() => { setActiveTab('tryouts'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'tryouts' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-tryouts"
          >
            <Trophy className="w-5 h-5" />
            Try Outs
          </button>
          <button
            onClick={() => { setActiveTab('event-tryouts'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'event-tryouts' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-event-tryouts"
          >
            <Calendar className="w-5 h-5" />
            Event Try Out
          </button>
          <button
            onClick={() => { setActiveTab('promos'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'promos' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-promos"
          >
            <Gift className="w-5 h-5" />
            Promos
          </button>
          <button
            onClick={() => { setActiveTab('kelas-zoom'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'kelas-zoom' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-zoom"
          >
            <MonitorPlay className="w-5 h-5" />
            Kelas Zoom
          </button>
          <button
            onClick={() => { setActiveTab('konten-landing'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'konten-landing' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-landing"
          >
            <Newspaper className="w-5 h-5" />
            Konten Landing
          </button>
          <button
            onClick={() => { setActiveTab('subscription-tiers'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'subscription-tiers' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-subscription-tiers"
          >
            <Crown className="w-5 h-5" />
            Subscription Tiers
          </button>
          <button
            onClick={() => { setActiveTab('users'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'users' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-users"
          >
            <Users className="w-5 h-5" />
            Pengaturan Peserta
          </button>
          <button
            onClick={() => { setActiveTab('exam-results'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'exam-results' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-exam-results"
          >
            <ClipboardList className="w-5 h-5" />
            Hasil Nilai Ujian
          </button>
          <button
            onClick={() => { setActiveTab('purchases'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'purchases' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-purchases"
          >
            <ShoppingCart className="w-5 h-5" />
            Konfirmasi Pembelian
          </button>
          <button
            onClick={() => { setActiveTab('materi'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'materi' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-materi"
          >
            <Upload className="w-5 h-5" />
            Upload Materi
          </button>
          <button
            onClick={() => { setActiveTab('ebooks'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'ebooks' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-ebooks"
          >
            <BookOpen className="w-5 h-5" />
            eBook
          </button>
          <button
            onClick={() => { setActiveTab('testimonials'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'testimonials' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-testimonials"
          >
            <Quote className="w-5 h-5" />
            Testimoni
          </button>
          <button
            onClick={() => { setActiveTab('program-images'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'program-images' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-program-images"
          >
            <ImageIcon className="w-5 h-5" />
            Gambar Program
          </button>
          <button
            onClick={() => { setActiveTab('email-list'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'email-list' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-email-list"
          >
            <Mail className="w-5 h-5" />
            Upload Email
          </button>
          <button
            onClick={() => { setActiveTab('analytics'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'analytics' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-analytics"
          >
            <BarChart3 className="w-5 h-5" />
            Analytics
          </button>
          <button
            onClick={() => { setActiveTab('import'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'import' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-import"
          >
            <FileUp className="w-5 h-5" />
            Import Soal
          </button>
          
          {/* Divider */}
          <div className="my-3 border-t border-slate-200" />
          
          <button
            onClick={() => { setActiveTab('admin-settings'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'admin-settings' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-admin-settings"
          >
            <UserCog className="w-5 h-5" />
            Setting Admin
          </button>
          <button
            onClick={() => { setActiveTab('change-password'); setMobileSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              activeTab === 'change-password' ? 'bg-[#4472C4]/10 text-[#4472C4] font-medium' : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="nav-change-password"
          >
            <KeyRound className="w-5 h-5" />
            Ganti Password
          </button>
        </nav>

        {/* User info and logout - always visible */}
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 p-2 mb-2">
            <div className="w-10 h-10 bg-[#4472C4] rounded-full flex items-center justify-center text-white font-bold text-sm">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate text-sm">{user?.name}</div>
              <div className="text-xs text-slate-500">Administrator</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/" className="flex-1">
              <Button variant="outline" size="sm" className="w-full" title="Beranda">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 text-red-500 hover:text-red-600 hover:bg-red-50 hover:border-red-200" 
              onClick={handleLogout} 
              data-testid="admin-logout"
              title="Keluar"
            >
              <LogOut className="w-4 h-4 mr-1" />
              <span className="text-xs">Keluar</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 p-4 md:p-6 pt-20 md:pt-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white" data-testid="stat-users">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Users</p>
                      <div className="text-3xl font-bold text-slate-900 mt-1">{stats.total_students || 0}</div>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white" data-testid="stat-questions">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Questions</p>
                      <div className="text-3xl font-bold text-slate-900 mt-1">{stats.total_questions || 0}</div>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white" data-testid="stat-tryouts">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Try Outs</p>
                      <div className="text-3xl font-bold text-slate-900 mt-1">{stats.total_tryouts || 0}</div>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white" data-testid="stat-revenue">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-600 font-medium">Pendapatan</p>
                      <div className="text-3xl font-bold text-slate-900 mt-1">
                        Rp {(stats.total_revenue || 0).toLocaleString('id-ID')}
                      </div>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Additional Stats Row */}
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Paket</p>
                      <div className="text-xl font-bold text-slate-900">{stats.total_packages || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Mata Pelajaran</p>
                      <div className="text-xl font-bold text-slate-900">{stats.total_subjects || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                      <Tag className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Kategori</p>
                      <div className="text-xl font-bold text-slate-900">{stats.total_categories || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                      <Video className="w-5 h-5 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Kelas Zoom</p>
                      <div className="text-xl font-bold text-slate-900">{stats.total_zoom_classes || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Gift className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Promo</p>
                      <div className="text-xl font-bold text-slate-900">{stats.total_promos || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Users</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {users.length === 0 ? (
                    <div className="text-center text-slate-500 py-4">Belum ada user</div>
                  ) : (
                    users.slice(0, 5).map((u, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center font-bold text-slate-600">
                            {u.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{u.name || 'Unknown'}</div>
                            <div className="text-sm text-slate-500">{u.email}</div>
                          </div>
                        </div>
                        <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                          {u.role}
                        </Badge>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <button
                    onClick={() => { setActiveTab('questions'); setQuestionDialog({ open: true, mode: 'create', data: null }); }}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
                  >
                    <Plus className="w-5 h-5 text-slate-400" />
                    <span className="font-medium text-slate-700">Add Question</span>
                  </button>
                  <button
                    onClick={() => { setActiveTab('tryouts'); setTryoutDialog({ open: true, mode: 'create', data: null }); }}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
                  >
                    <Plus className="w-5 h-5 text-slate-400" />
                    <span className="font-medium text-slate-700">Create Try Out</span>
                  </button>
                  <button
                    onClick={() => { setActiveTab('packages'); setPackageDialog({ open: true, mode: 'create', data: null }); }}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
                  >
                    <Plus className="w-5 h-5 text-slate-400" />
                    <span className="font-medium text-slate-700">Add Package</span>
                  </button>
                  <button
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
                    onClick={() => {
                      setActiveTab('promos');
                      setPromoDialog({ open: true, mode: 'create', data: null });
                    }}
                  >
                    <Plus className="w-5 h-5 text-slate-400" />
                    <span className="font-medium text-slate-700">Create Promo</span>
                  </button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Questions Tab (Bank Soal) */}
        {activeTab === 'questions' && (
          <BankSoalTab 
            questions={questions}
            stats={stats}
            questionFilter={questionFilter}
            setQuestionFilter={setQuestionFilter}
            questionSearch={questionSearch}
            setQuestionSearch={setQuestionSearch}
            filteredQuestions={filteredQuestions}
            subjects={subjects}
            onAddQuestion={() => setQuestionDialog({ open: true, mode: 'create', data: null })}
            onEditQuestion={(q) => setQuestionDialog({ open: true, mode: 'edit', data: q })}
            onDeleteQuestion={(id) => setDeleteQuestionDialog({ open: true, id })}
            onBulkDelete={handleBulkDeleteQuestions}
            onDownloadTemplate={handleDownloadTemplate}
            onImportCSV={() => fileInputRef.current?.click()}
            onExportQuestions={handleExportQuestions}
            fileInputRef={fileInputRef}
            handleImportCSV={handleImportCSV}
            setQuestionDialog={setQuestionDialog}
            pagination={questionPagination}
            onPageChange={(page) => loadQuestions(page)}
            loadingQuestions={loadingQuestions}
            questionStats={questionStats}
          />
        )}

        {/* Paket Try Out Tab - Shows Packages (Basic, Silver, Gold, Platinum) */}
        {activeTab === 'paket-tryout' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Paket Try Out</h1>
                <p className="text-slate-500 mt-1">Kelola paket bundling (Basic, Silver, Gold, Platinum)</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setPaketGenDialog({ open: true })}
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                  data-testid="gen-paket-btn"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Generate Paket Lengkap
                </Button>
                <Button
                  onClick={() => setPackageDialog({ open: true, mode: 'create', data: null })}
                  className="bg-[#4472C4] hover:bg-[#3861a8]"
                  data-testid="add-paket-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Paket
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.length === 0 ? (
                <Card className="col-span-full p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Paket</h3>
                  <p className="text-slate-500">Tambahkan paket baru (Basic, Silver, Gold, Platinum)</p>
                </Card>
              ) : (
                packages.map((p, i) => (
                  <Card key={p.id} className="hover:shadow-lg transition-all" data-testid={`paket-item-${i}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <Badge 
                          variant="secondary" 
                          className={
                            p.name?.toLowerCase().includes('platinum') ? 'bg-purple-100 text-purple-700' :
                            p.name?.toLowerCase().includes('gold') ? 'bg-yellow-100 text-yellow-700' :
                            p.name?.toLowerCase().includes('silver') ? 'bg-gray-100 text-gray-700' :
                            'bg-blue-100 text-blue-700'
                          }
                        >
                          {p.name}
                        </Badge>
                        <button
                          onClick={async () => {
                            try {
                              await packageAdminAPI.toggleStatus(p.id);
                              loadData();
                              toast.success(`Paket ${p.is_active ? 'dinonaktifkan' : 'diaktifkan'}`);
                            } catch (err) {
                              toast.error('Gagal mengubah status');
                            }
                          }}
                          className={`w-10 h-5 rounded-full transition-colors ${p.is_active ? 'bg-green-500' : 'bg-slate-300'}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${p.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      <CardTitle className="text-lg mt-2">{p.name}</CardTitle>
                      <CardDescription className="line-clamp-2">{p.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Harga:</span>
                          <span className="font-semibold text-green-600">{formatPrice(p.price)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Try Out Termasuk:</span>
                          <span className="font-medium">{p.tryout_ids?.length || 0} Try Out</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Status:</span>
                          <Badge variant={p.is_active ? 'default' : 'secondary'}>
                            {p.is_active ? 'Aktif' : 'Nonaktif'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPackageDialog({ open: true, mode: 'edit', data: p })}
                          className="flex-1"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={async () => {
                            if (window.confirm('Hapus paket ini?')) {
                              try {
                                await packageAdminAPI.delete(p.id);
                                loadData();
                                toast.success('Paket dihapus');
                              } catch (err) {
                                toast.error('Gagal menghapus paket');
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* TryOuts Tab - Standalone Try Outs */}
        {activeTab === 'tryouts' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Try Outs</h1>
                <p className="text-slate-500 mt-1">Kelola try out standalone untuk siswa</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setAutoGenDialog({ open: true })}
                  variant="outline"
                  className="border-[#4472C4] text-[#4472C4] hover:bg-[#4472C4]/10"
                  data-testid="auto-gen-tryout-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Auto-Generate
                </Button>
                <Button
                  onClick={() => setTryoutDialog({ open: true, mode: 'create', data: null })}
                  className="bg-[#4472C4] hover:bg-[#3861a8]"
                  data-testid="add-tryout-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Try Out
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {tryouts.length === 0 ? (
                <Card className="col-span-full p-8 text-center">
                  <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Belum Ada Try Out</h3>
                  <p className="text-sm text-slate-500">Tambahkan try out baru</p>
                </Card>
              ) : (
                tryouts.map((t, i) => (
                  <Card key={t.id} className="hover:shadow-md transition-all text-xs" data-testid={`tryout-item-${i}`}>
                    <CardHeader className="p-2 pb-1">
                      <div className="flex items-start justify-between gap-1">
                        <Badge variant="secondary" className="text-[9px] px-1 py-0">{t.category}</Badge>
                        <button
                          onClick={async () => {
                            try {
                              await tryoutAdminAPI.toggleStatus(t.id);
                              loadData();
                              toast.success(`Try Out ${t.is_active ? 'dinonaktifkan' : 'diaktifkan'}`);
                            } catch (err) {
                              toast.error('Gagal mengubah status');
                            }
                          }}
                          className={`px-1 py-0.5 rounded text-[9px] font-medium cursor-pointer ${
                            t.is_active 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`toggle-tryout-${i}`}
                        >
                          {t.is_active ? 'Aktif' : 'Nonaktif'}
                        </button>
                      </div>
                      <CardTitle className="text-xs font-semibold line-clamp-2 mt-1">{t.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-2 pt-0">
                      <div className="space-y-0.5 text-[10px] text-slate-500 mb-2">
                        <div className="flex justify-between">
                          <span>Soal:</span>
                          <span className="font-medium text-slate-700">{t.question_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Durasi:</span>
                          <span className="font-medium text-slate-700">{t.duration_minutes}m</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Harga:</span>
                          <span className={`font-medium ${t.price === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                            {t.price === 0 ? 'GRATIS' : formatPrice(t.price)}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="flex-1 h-6 text-[10px] px-1"
                          onClick={() => { setQuestionSelectDialog({ open: true, tryoutId: t.id, selectedIds: t.question_ids || [] }); }}
                          data-testid={`select-questions-${i}`}
                        >
                          Pilih Soal
                        </Button>
                        <Button variant="outline" size="sm" className="h-6 w-6 p-0 bg-blue-50 border-blue-200 text-blue-600"
                          onClick={() => handlePreviewTryoutQuestions(t)}
                          data-testid={`preview-tryout-${i}`}
                          title="Preview Semua Soal"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-6 w-6 p-0 bg-amber-50 border-amber-200 text-amber-600"
                          onClick={() => setTrimQuestionDialog({ open: true, tryout: t, targetCount: t.question_count })}
                          data-testid={`trim-tryout-${i}`}
                          title="Perbaiki Jumlah Soal"
                        >
                          <Scissors className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-6 w-6 p-0 bg-purple-50 border-purple-200 text-purple-600"
                          onClick={() => setSubjectConfigDialog({ open: true, tryout: t })}
                          data-testid={`config-tryout-${i}`}
                          title="Konfigurasi"
                        >
                          <Settings className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-6 w-6 p-0"
                          onClick={() => setTryoutDialog({ open: true, mode: 'edit', data: t })}
                          data-testid={`edit-tryout-${i}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteTryoutDialog({ open: true, id: t.id })}
                          data-testid={`delete-tryout-${i}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Packages (Legacy)</h1>
                <p className="text-slate-500 mt-1">Kelola paket bundling try out</p>
              </div>
              <Button
                onClick={() => setPackageDialog({ open: true, mode: 'create', data: null })}
                className="bg-[#4472C4] hover:bg-[#3861a8]"
                data-testid="add-package-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Paket
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.length === 0 ? (
                <Card className="col-span-full p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Paket</h3>
                  <p className="text-slate-500">Tambahkan paket baru</p>
                </Card>
              ) : (
                packages.map((p, i) => (
                  <Card key={p.id} className="hover:shadow-lg transition-all" data-testid={`package-item-${i}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        {p.discount_percent > 0 && (
                          <Badge className="bg-red-100 text-red-700">-{p.discount_percent}%</Badge>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              await packageAdminAPI.toggleStatus(p.id);
                              loadData();
                              toast.success(`Paket ${p.is_active ? 'dinonaktifkan' : 'diaktifkan'}`);
                            } catch (err) {
                              toast.error('Gagal mengubah status');
                            }
                          }}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                            p.is_active 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`toggle-package-${i}`}
                        >
                          {p.is_active ? 'Aktif' : 'Nonaktif'}
                        </button>
                      </div>
                      <CardTitle className="text-lg">{p.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{p.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-slate-500 mb-4">
                        <div className="flex justify-between">
                          <span>Try Out:</span>
                          <span className="font-medium text-slate-900">{p.tryout_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Harga:</span>
                          <div>
                            {p.discount_percent > 0 && (
                              <span className="text-slate-400 line-through mr-2">{formatPrice(p.price)}</span>
                            )}
                            <span className="font-bold text-[#4472C4]">{formatPrice(p.final_price)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setTryoutSelectDialog({
                              open: true,
                              packageId: p.id,
                              selectedIds: p.tryout_ids || []
                            });
                          }}
                          data-testid={`select-tryouts-${i}`}
                        >
                          Pilih Try Out
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPackageDialog({ open: true, mode: 'edit', data: p })}
                          data-testid={`edit-package-${i}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeletePackageDialog({ open: true, id: p.id })}
                          data-testid={`delete-package-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Promos Tab */}
        {activeTab === 'promos' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Manajemen Promo</h1>
                <p className="text-slate-500 mt-1">Kelola kode promo dan diskon</p>
              </div>
              <Button
                onClick={() => setPromoDialog({ open: true, mode: 'create', data: null })}
                className="bg-[#4472C4] hover:bg-[#3861a8]"
                data-testid="add-promo-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Promo
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {promos.length === 0 ? (
                <Card className="col-span-full p-12 text-center">
                  <Tag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Promo</h3>
                  <p className="text-slate-500">Tambahkan promo baru</p>
                </Card>
              ) : (
                promos.map((p, i) => (
                  <Card key={p.id} className="hover:shadow-lg transition-all" data-testid={`promo-item-${i}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <Badge className="bg-[#FF6B35] text-white">{p.code}</Badge>
                        <Badge variant={p.is_active ? 'default' : 'secondary'}>
                          {p.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{p.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{p.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-slate-500 mb-4">
                        <div className="flex justify-between">
                          <span>Diskon:</span>
                          <span className="font-medium text-slate-900">
                            {p.discount_percent > 0 ? `${p.discount_percent}%` : formatPrice(p.discount_amount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Min. Pembelian:</span>
                          <span className="font-medium text-slate-900">{formatPrice(p.min_purchase)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Penggunaan:</span>
                          <span className="font-medium text-slate-900">
                            {p.usage_count} / {p.usage_limit || '∞'}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => setPromoDialog({ open: true, mode: 'edit', data: p })}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeletePromoDialog({ open: true, id: p.id })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Kelas Zoom Tab */}
        {activeTab === 'kelas-zoom' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Kelas Zoom</h1>
                <p className="text-slate-500 mt-1">Kelola jadwal kelas online</p>
              </div>
              <Button
                onClick={() => setZoomDialog({ open: true, mode: 'create', data: null })}
                className="bg-[#4472C4] hover:bg-[#3861a8]"
                data-testid="add-zoom-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Kelas
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {zoomClasses.length === 0 ? (
                <Card className="col-span-full p-12 text-center">
                  <MonitorPlay className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Kelas Zoom</h3>
                  <p className="text-slate-500">Tambahkan jadwal kelas baru</p>
                </Card>
              ) : (
                zoomClasses.map((c, i) => (
                  <Card key={c.id} className="hover:shadow-lg transition-all" data-testid={`zoom-item-${i}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <Badge variant="secondary">{c.program}</Badge>
                        <Badge variant={c.is_active ? 'default' : 'secondary'}>
                          {c.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{c.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{c.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-slate-500 mb-4">
                        <div className="flex justify-between">
                          <span>Instruktur:</span>
                          <span className="font-medium text-slate-900">{c.instructor}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Jadwal:</span>
                          <span className="font-medium text-slate-900">{c.schedule_date} {c.schedule_time}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Durasi:</span>
                          <span className="font-medium text-slate-900">{c.duration_minutes} menit</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Peserta:</span>
                          <span className="font-medium text-slate-900">{c.registered_count} / {c.max_participants}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href={c.zoom_link} target="_blank" rel="noopener noreferrer" className="flex-1">
                          <Button variant="outline" size="sm" className="w-full">
                            <LinkIcon className="w-4 h-4 mr-1" />
                            Link Zoom
                          </Button>
                        </a>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setZoomDialog({ open: true, mode: 'edit', data: c })}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteZoomDialog({ open: true, id: c.id })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Event Try Outs Tab */}
        {activeTab === 'event-tryouts' && (
          <EventTryOutTab />
        )}

        {/* Konten Landing Tab */}
        {activeTab === 'konten-landing' && (
          <LandingSettingsTab />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Manajemen Users</h1>
                <p className="text-sm text-slate-500">Kelola pengguna aplikasi</p>
              </div>
            </div>

            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Nama</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Email</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Role</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Terdaftar</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center">
                          <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <h3 className="font-semibold text-slate-900 mb-1 text-sm">Belum Ada User</h3>
                          <p className="text-xs text-slate-500">User akan muncul setelah mendaftar</p>
                        </td>
                      </tr>
                    ) : (
                      users.map((u, i) => (
                        <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${i}`}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center text-xs font-bold text-slate-600">
                                {u.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <span className="font-medium text-slate-900 text-sm block">{u.name}</span>
                                {u.phone && <span className="text-[10px] text-slate-500">{u.phone}</span>}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-slate-600 text-sm">{u.email}</td>
                          <td className="px-3 py-2">
                            <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="text-xs px-2 py-0">
                              {u.role}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-slate-500 text-xs">
                            {new Date(u.created_at).toLocaleDateString('id-ID')}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-0.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => setUserDialog({ open: true, mode: 'edit', data: u })}
                                data-testid={`edit-user-${i}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteUserDialog({ open: true, id: u.id })}
                                disabled={u.role === 'admin' && u.id === user?.id}
                                data-testid={`delete-user-${i}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Subjects (Mata Pelajaran) Tab */}
        {activeTab === 'subjects' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Mata Pelajaran</h1>
                <p className="text-slate-500">Kelola mata pelajaran untuk setiap program</p>
              </div>
              <Button 
                className="bg-[#4472C4] hover:bg-[#3861a8]"
                onClick={() => setSubjectDialog({ open: true, mode: 'create', data: null })}
                data-testid="add-subject-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Mata Pelajaran
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <Select value={subjectFilter || "all"} onValueChange={(v) => setSubjectFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48" data-testid="subject-program-filter">
                  <SelectValue placeholder="Semua Program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Program</SelectItem>
                  {PROGRAMS.map(prog => (
                    <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">
                {subjects.filter(s => !subjectFilter || s.program === subjectFilter).length} mata pelajaran
              </span>
            </div>

            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Program</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Nama Mata Pelajaran</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Deskripsi</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Status</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {subjects.filter(s => !subjectFilter || s.program === subjectFilter).length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-slate-500">
                          Belum ada mata pelajaran. Tambahkan mata pelajaran baru.
                        </td>
                      </tr>
                    ) : (
                      subjects.filter(s => !subjectFilter || s.program === subjectFilter).map((subj) => (
                        <tr key={subj.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <Badge variant="outline">{subj.program}</Badge>
                          </td>
                          <td className="p-4 font-medium text-slate-900">{subj.name}</td>
                          <td className="p-4 text-slate-500 text-sm">{subj.description || '-'}</td>
                          <td className="p-4">
                            <Badge className={subj.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                              {subj.is_active ? 'Aktif' : 'Nonaktif'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setSubjectDialog({ open: true, mode: 'edit', data: subj })}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteSubjectDialog({ open: true, id: subj.id })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Categories (Kategori) Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Kategori</h1>
                <p className="text-slate-500">Kelola kategori untuk setiap mata pelajaran</p>
              </div>
              <Button 
                className="bg-[#4472C4] hover:bg-[#3861a8]"
                onClick={() => setCategoryDialog({ open: true, mode: 'create', data: null })}
                data-testid="add-category-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Kategori
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <Select 
                value={categoryFilter.program || "all"} 
                onValueChange={(v) => setCategoryFilter(prev => ({ ...prev, program: v === "all" ? "" : v, subject: '' }))}
              >
                <SelectTrigger className="w-48" data-testid="category-program-filter">
                  <SelectValue placeholder="Semua Program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Program</SelectItem>
                  {PROGRAMS.map(prog => (
                    <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categoryFilter.program && (
                <Select 
                  value={categoryFilter.subject || "all"} 
                  onValueChange={(v) => setCategoryFilter(prev => ({ ...prev, subject: v === "all" ? "" : v }))}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Semua Mapel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Mapel</SelectItem>
                    {subjects.filter(s => s.program === categoryFilter.program).map(s => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <span className="text-sm text-slate-500">
                {categories.filter(c => 
                  (!categoryFilter.program || c.program === categoryFilter.program) &&
                  (!categoryFilter.subject || c.subject === categoryFilter.subject)
                ).length} kategori
              </span>
            </div>

            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Program</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Mata Pelajaran</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Nama Kategori</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Status</th>
                      <th className="text-left p-4 font-medium text-slate-600 text-sm">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {categories.filter(c => 
                      (!categoryFilter.program || c.program === categoryFilter.program) &&
                      (!categoryFilter.subject || c.subject === categoryFilter.subject)
                    ).length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-slate-500">
                          Belum ada kategori. Tambahkan kategori baru.
                        </td>
                      </tr>
                    ) : (
                      categories.filter(c => 
                        (!categoryFilter.program || c.program === categoryFilter.program) &&
                        (!categoryFilter.subject || c.subject === categoryFilter.subject)
                      ).map((cat) => (
                        <tr key={cat.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <Badge variant="outline">{cat.program}</Badge>
                          </td>
                          <td className="p-4 text-slate-700">{cat.subject}</td>
                          <td className="p-4 font-medium text-slate-900">{cat.name}</td>
                          <td className="p-4">
                            <Badge className={cat.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                              {cat.is_active ? 'Aktif' : 'Nonaktif'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setCategoryDialog({ open: true, mode: 'edit', data: cat })}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteCategoryDialog({ open: true, id: cat.id })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard />
        )}

        {/* Import Soal Tab */}
        {activeTab === 'import' && (
          <BatchImportTab />
        )}

        {/* Upload Materi Tab */}
        {activeTab === 'materi' && (
          <MateriTab />
        )}

        {/* eBooks Tab */}
        {activeTab === 'ebooks' && (
          <div className="space-y-6" data-testid="ebooks-tab">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Kelola eBook</h1>
                <p className="text-slate-500 mt-1">Kelola koleksi eBook untuk dijual ke user</p>
              </div>
              <Button onClick={() => setEbookDialog({ open: true, mode: 'create', data: null })} data-testid="add-ebook-btn">
                <Plus className="w-4 h-4 mr-2" />
                Tambah eBook
              </Button>
            </div>

            {/* eBooks Grid */}
            {ebooks.length === 0 ? (
              <Card className="p-8 text-center">
                <BookOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Belum ada eBook. Tambahkan eBook baru.</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {ebooks.map((ebook) => (
                  <Card key={ebook.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`ebook-card-${ebook.id}`}>
                    <div className="aspect-[3/4] bg-gradient-to-br from-blue-500 to-purple-600 relative">
                      {ebook.cover_url ? (
                        <img src={ebook.cover_url} alt={ebook.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <BookOpen className="w-20 h-20 text-white/50" />
                        </div>
                      )}
                      {ebook.is_featured && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">
                          Featured
                        </div>
                      )}
                      {!ebook.is_active && (
                        <div className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded">
                          Nonaktif
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-slate-900 line-clamp-2">{ebook.title}</h3>
                      {ebook.author && <p className="text-sm text-slate-500 mt-1">{ebook.author}</p>}
                      {ebook.program && (
                        <Badge variant="secondary" className="mt-2 text-xs">{ebook.program}</Badge>
                      )}
                      <div className="flex items-center gap-2 mt-3">
                        <span className="font-bold text-[#4472C4]">
                          {ebook.price > 0 ? formatPrice(ebook.price) : 'Gratis'}
                        </span>
                        {ebook.original_price && ebook.original_price > ebook.price && (
                          <span className="text-sm text-slate-400 line-through">
                            {formatPrice(ebook.original_price)}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => setEbookDialog({ open: true, mode: 'edit', data: ebook })}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteEbookDialog({ open: true, id: ebook.id })}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <TestimonialsTab />
        )}

        {/* Program Images Tab */}
        {activeTab === 'program-images' && (
          <ProgramImagesTab />
        )}

        {/* Email List Tab */}
        {activeTab === 'email-list' && (
          <EmailListTab />
        )}

        {/* Admin Settings Tab */}
        {activeTab === 'admin-settings' && (
          <AdminSettingsTab />
        )}

        {/* Exam Results Tab */}
        {activeTab === 'exam-results' && (
          <ExamResultsTab />
        )}

        {/* Purchase Management Tab */}
        {activeTab === 'purchases' && (
          <PurchaseManagementTab />
        )}

        {/* Change Password Tab */}
        {activeTab === 'change-password' && (
          <ChangePasswordTab />
        )}

        {/* Subscription Tiers Tab */}
        {activeTab === 'subscription-tiers' && (
          <div className="space-y-6" data-testid="subscription-tiers-tab">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-slate-900">Subscription Tiers</h1>
              <Button onClick={() => setTierDialog({ open: true, mode: 'create', data: null })} data-testid="add-tier-btn">
                <Plus className="w-4 h-4 mr-2" />
                Tambah Tier
              </Button>
            </div>

            {/* Tier Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {subscriptionTiers.map((tier) => (
                <Card key={tier.id} className="relative overflow-hidden" data-testid={`tier-card-${tier.name}`}>
                  <div 
                    className="absolute top-0 left-0 right-0 h-2" 
                    style={{ backgroundColor: tier.color || '#4472C4' }}
                  />
                  <CardHeader className="pt-6">
                    <div className="flex items-center justify-between">
                      <Badge 
                        variant={tier.is_active ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {tier.is_active ? 'Aktif' : 'Nonaktif'}
                      </Badge>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setTierDialog({ open: true, mode: 'edit', data: tier })}
                          data-testid={`edit-tier-${tier.name}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteTierDialog({ open: true, id: tier.id })}
                          data-testid={`delete-tier-${tier.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <CardTitle className="text-xl" style={{ color: tier.color || '#4472C4' }}>
                      {tier.display_name}
                    </CardTitle>
                    <CardDescription>{tier.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900 mb-4">
                      {formatPrice(tier.price)}
                      <span className="text-sm font-normal text-slate-500">/{tier.duration_days} hari</span>
                    </div>
                    <ul className="space-y-2 text-sm">
                      {tier.features?.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t space-y-1 text-xs text-slate-500">
                      <p>Max Try Out: {tier.max_tryouts_per_month === -1 ? 'Unlimited' : `${tier.max_tryouts_per_month}/bulan`}</p>
                      {tier.includes_zoom && <p>Zoom: {tier.zoom_sessions_per_month} sesi/bulan</p>}
                      {tier.includes_ebook && <p>E-Book: Ya</p>}
                      {tier.includes_wag && <p>WAG: {tier.wag_type === 'vip' ? 'VIP' : 'Regular'}</p>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {subscriptionTiers.length === 0 && (
              <Card className="p-12 text-center">
                <Crown className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Belum ada Subscription Tier</h3>
                <p className="text-slate-500 mb-4">Mulai dengan membuat tier langganan untuk platform Anda.</p>
                <Button onClick={() => setTierDialog({ open: true, mode: 'create', data: null })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Tier Pertama
                </Button>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Question Dialog */}
      <QuestionDialog
        open={questionDialog.open}
        mode={questionDialog.mode}
        data={questionDialog.data}
        tryouts={tryouts}
        subjects={subjects}
        categories={categories}
        onClose={() => setQuestionDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveQuestion}
      />

      {/* TryOut Dialog */}
      <TryoutDialog
        open={tryoutDialog.open}
        mode={tryoutDialog.mode}
        data={tryoutDialog.data}
        subjects={subjects}
        onClose={() => setTryoutDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveTryout}
      />

      {/* Subject Config Dialog */}
      <SubjectConfigDialog
        open={subjectConfigDialog.open}
        onOpenChange={(open) => !open && setSubjectConfigDialog({ open: false, tryout: null })}
        tryout={subjectConfigDialog.tryout}
        onSaved={loadData}
      />

      {/* Auto Generate Try Out Dialog */}
      <AutoGenerateDialog
        open={autoGenDialog.open}
        onClose={() => setAutoGenDialog({ open: false })}
        onSuccess={() => {
          setAutoGenDialog({ open: false });
          loadData();
        }}
      />

      {/* Package Dialog */}
      <PackageDialog
        open={packageDialog.open}
        mode={packageDialog.mode}
        data={packageDialog.data}
        onClose={() => setPackageDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSavePackage}
        tryouts={tryouts}
      />

      {/* Promo Dialog */}
      <PromoDialog
        open={promoDialog.open}
        mode={promoDialog.mode}
        data={promoDialog.data}
        onClose={() => setPromoDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSavePromo}
      />

      {/* Zoom Class Dialog */}
      <ZoomClassDialog
        open={zoomDialog.open}
        mode={zoomDialog.mode}
        data={zoomDialog.data}
        onClose={() => setZoomDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveZoomClass}
      />

      {/* Landing Content Dialog */}
      <LandingContentDialog
        open={landingDialog.open}
        mode={landingDialog.mode}
        data={landingDialog.data}
        onClose={() => setLandingDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveLandingContent}
      />

      {/* Subject Dialog */}
      <SubjectDialog
        open={subjectDialog.open}
        mode={subjectDialog.mode}
        data={subjectDialog.data}
        onClose={() => setSubjectDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveSubject}
      />

      {/* Category Dialog */}
      <CategoryDialog
        open={categoryDialog.open}
        mode={categoryDialog.mode}
        data={categoryDialog.data}
        subjects={subjects}
        onClose={() => setCategoryDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveCategory}
      />

      {/* eBook Dialog */}
      <EbookDialog
        open={ebookDialog.open}
        mode={ebookDialog.mode}
        data={ebookDialog.data}
        onClose={() => setEbookDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveEbook}
      />

      {/* Question Selection Dialog */}
      <SelectionDialog
        open={questionSelectDialog.open}
        title="Pilih Soal untuk Try Out"
        items={questions}
        selectedIds={questionSelectDialog.selectedIds}
        onClose={() => setQuestionSelectDialog({ open: false, tryoutId: null, selectedIds: [] })}
        onSave={handleSaveQuestionSelection}
        onSelectionChange={(ids) => setQuestionSelectDialog(prev => ({ ...prev, selectedIds: ids }))}
        renderItem={(q) => (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary" className="text-xs">{q.program || q.category}</Badge>
              <Badge variant="outline" className="text-xs">{q.mapel || q.difficulty}</Badge>
            </div>
            <p className="text-sm line-clamp-2">{stripHtmlForPreview(q.text)}</p>
          </div>
        )}
      />

      {/* TryOut Selection Dialog */}
      <SelectionDialog
        open={tryoutSelectDialog.open}
        title="Pilih Try Out untuk Paket"
        items={tryouts}
        selectedIds={tryoutSelectDialog.selectedIds}
        onClose={() => setTryoutSelectDialog({ open: false, packageId: null, selectedIds: [] })}
        onSave={handleSaveTryoutSelection}
        onSelectionChange={(ids) => setTryoutSelectDialog(prev => ({ ...prev, selectedIds: ids }))}
        renderItem={(t) => (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary" className="text-xs">{t.category}</Badge>
            </div>
            <p className="font-medium">{t.title}</p>
            <p className="text-sm text-slate-500">{t.question_count} soal - {t.duration_minutes} menit</p>
          </div>
        )}
      />

      {/* Delete Dialogs */}
      <AlertDialog open={deleteQuestionDialog.open} onOpenChange={(o) => !o && setDeleteQuestionDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Soal</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus soal ini? Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteQuestion} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteTryoutDialog.open} onOpenChange={(o) => !o && setDeleteTryoutDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Try Out</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus try out ini?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTryout} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletePackageDialog.open} onOpenChange={(o) => !o && setDeletePackageDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Paket</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus paket ini?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePackage} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletePromoDialog.open} onOpenChange={(o) => !o && setDeletePromoDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Promo</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus promo ini?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePromo} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteZoomDialog.open} onOpenChange={(o) => !o && setDeleteZoomDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Kelas Zoom</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus kelas zoom ini?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteZoomClass} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteLandingDialog.open} onOpenChange={(o) => !o && setDeleteLandingDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Konten</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus konten ini?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLandingContent} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteUserDialog.open} onOpenChange={(o) => !o && setDeleteUserDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus User</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus user ini?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Edit Dialog */}
      <UserEditDialog
        open={userDialog.open}
        data={userDialog.data}
        onClose={() => setUserDialog({ open: false, mode: 'edit', data: null })}
        onSave={handleSaveUser}
      />

      {/* Delete Subject Dialog */}
      <AlertDialog open={deleteSubjectDialog.open} onOpenChange={(o) => !o && setDeleteSubjectDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Mata Pelajaran</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus mata pelajaran ini? Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSubject} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Dialog */}
      <AlertDialog open={deleteCategoryDialog.open} onOpenChange={(o) => !o && setDeleteCategoryDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Kategori</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus kategori ini? Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete eBook Dialog */}
      <AlertDialog open={deleteEbookDialog.open} onOpenChange={(o) => !o && setDeleteEbookDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus eBook</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus eBook ini? Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEbook} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Subscription Tier Dialog */}
      <AlertDialog open={deleteTierDialog.open} onOpenChange={(o) => !o && setDeleteTierDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Subscription Tier</AlertDialogTitle>
            <AlertDialogDescription>Apakah Anda yakin ingin menghapus subscription tier ini? Pengguna yang sudah berlangganan tier ini mungkin terpengaruh.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTier} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Subscription Tier Dialog */}
      <SubscriptionTierDialog
        open={tierDialog.open}
        mode={tierDialog.mode}
        data={tierDialog.data}
        onClose={() => setTierDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSaveTier}
      />

      {/* Generate Paket Lengkap Dialog */}
      <GeneratePaketDialog
        open={paketGenDialog.open}
        onClose={() => setPaketGenDialog({ open: false })}
        onSuccess={() => {
          setPaketGenDialog({ open: false });
          loadData();
        }}
      />

      {/* Tryout Preview Dialog - Preview All Questions in a Tryout */}
      <Dialog open={tryoutPreviewDialog.open} onOpenChange={(o) => !o && setTryoutPreviewDialog({ open: false, tryout: null, questions: [], loading: false })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Preview Soal - {tryoutPreviewDialog.tryout?.title || tryoutPreviewDialog.tryout?.name}
            </DialogTitle>
            <DialogDescription>
              {tryoutPreviewDialog.tryout?.category} | Total: {tryoutPreviewDialog.questions.length} soal
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {tryoutPreviewDialog.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-slate-600">Memuat soal...</span>
              </div>
            ) : tryoutPreviewDialog.questions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Belum ada soal dalam try out ini</p>
              </div>
            ) : (
              <div className="space-y-6">
                {tryoutPreviewDialog.questions.map((q, idx) => {
                  const hasCorrectAnswer = !['multiple_choice_weighted', 'likert', 'ab'].includes(q.question_type);
                  
                  return (
                    <div key={q.id} className="border rounded-lg p-4 bg-white">
                      {/* Question Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </span>
                        <Badge variant="secondary" className="text-xs">{q.mapel || 'Umum'}</Badge>
                        {q.kategori && <Badge variant="outline" className="text-xs">{q.kategori}</Badge>}
                        <Badge variant="outline" className="text-xs ml-auto">
                          {q.question_type === 'multiple_choice_weighted' ? 'TKP/Berbobot' : 
                           q.question_type === 'multiple_choice_custom' ? 'Custom' :
                           q.question_type === 'yes_no' ? 'Ya/Tidak' :
                           q.question_type === 'likert' ? 'Likert' :
                           q.question_type === 'ab' ? 'A/B' : 'PG'}
                        </Badge>
                      </div>

                      {/* Question Text */}
                      <div 
                        className="prose prose-sm max-w-none mb-4 admin-question-preview"
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(q.text) }}
                      />

                      {/* Options */}
                      {q.options && q.options.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {q.options.map((opt, optIdx) => {
                            // TKP, Likert, A/B - NO correct answer, only weights
                            const qType = q.question_type || 'multiple_choice';
                            const isWeightedType = ['multiple_choice_weighted', 'likert', 'ab'].includes(qType);
                            const showAsCorrect = hasCorrectAnswer && opt.is_correct;
                            
                            // For TKP: highlight highest weight
                            const maxWeight = isWeightedType ? Math.max(...q.options.map(o => o.weight || 0)) : 0;
                            const isHighestWeight = isWeightedType && (opt.weight || 0) === maxWeight && maxWeight > 0;
                            
                            return (
                              <div 
                                key={opt.id || optIdx}
                                className={`flex items-start gap-2 p-2 rounded border ${
                                  showAsCorrect ? 'bg-green-50 border-green-300' : 
                                  isHighestWeight ? 'bg-blue-50 border-blue-300' :
                                  'bg-slate-50 border-slate-200'
                                }`}
                              >
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                  showAsCorrect ? 'bg-green-600 text-white' : 
                                  isHighestWeight ? 'bg-blue-600 text-white' :
                                  'bg-slate-300 text-slate-600'
                                }`}>
                                  {String.fromCharCode(65 + optIdx)}
                                </span>
                                <div className="flex-1">
                                  <span 
                                    className="text-sm"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(opt.text) }}
                                  />
                                  {/* Show weight for weighted types */}
                                  {isWeightedType && (
                                    <span className={`ml-2 text-xs font-medium ${(opt.weight || 0) >= 4 ? 'text-green-600' : (opt.weight || 0) >= 2 ? 'text-blue-600' : 'text-slate-500'}`}>
                                      (Bobot: {opt.weight || 0})
                                    </span>
                                  )}
                                </div>
                                {showAsCorrect && <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />}
                                {isHighestWeight && <Star className="w-4 h-4 text-blue-600 shrink-0 fill-blue-600" />}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Explanation */}
                      {q.explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-3">
                          <p className="text-xs text-blue-600 font-medium mb-1">Pembahasan:</p>
                          <div 
                            className="text-sm text-slate-700 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizeHTML(q.explanation) }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setTryoutPreviewDialog({ open: false, tryout: null, questions: [], loading: false })}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trim Question Count Dialog */}
      <Dialog open={trimQuestionDialog.open} onOpenChange={(o) => !o && setTrimQuestionDialog({ open: false, tryout: null, targetCount: 0 })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-amber-600" />
              Perbaiki Jumlah Soal
            </DialogTitle>
            <DialogDescription>
              {trimQuestionDialog.tryout?.title || trimQuestionDialog.tryout?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Jumlah soal saat ini:</strong> {trimQuestionDialog.tryout?.question_count || 0} soal
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Fitur ini akan memotong jumlah soal dari depan. Soal yang melebihi target akan dihapus dari Try Out.
              </p>
            </div>
            
            <div>
              <Label htmlFor="targetCount">Jumlah Soal yang Diinginkan</Label>
              <Input
                id="targetCount"
                type="number"
                min="1"
                max={trimQuestionDialog.tryout?.question_count || 100}
                value={trimQuestionDialog.targetCount}
                onChange={(e) => setTrimQuestionDialog(prev => ({ ...prev, targetCount: parseInt(e.target.value) || 0 }))}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Contoh: TWK = 30, TIU = 35, TKP = 45, Matematika = 40
              </p>
            </div>
            
            {trimQuestionDialog.targetCount > 0 && trimQuestionDialog.tryout?.question_count > trimQuestionDialog.targetCount && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  <strong>{(trimQuestionDialog.tryout?.question_count || 0) - trimQuestionDialog.targetCount} soal</strong> akan dihapus dari Try Out ini.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTrimQuestionDialog({ open: false, tryout: null, targetCount: 0 })}>
              Batal
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => handleTrimTryoutQuestions(trimQuestionDialog.tryout?.id, trimQuestionDialog.targetCount)}
              disabled={trimQuestionDialog.targetCount <= 0 || trimQuestionDialog.targetCount >= (trimQuestionDialog.tryout?.question_count || 0)}
            >
              <Scissors className="w-4 h-4 mr-2" />
              Perbaiki Jumlah Soal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Question Dialog Component with full form
function QuestionDialog({ open, mode, data, onClose, onSave, tryouts = [], subjects = [], categories = [] }) {
  const [formData, setFormData] = useState({
    program: '',
    mapel: '',
    kategori: '',
    question_type: 'multiple_choice',
    difficulty: 'medium',
    time_limit: '60',
    text: '',
    explanation: '',
    points: 10,
    likert_scale: 5,
    access_type: 'gratis',  // gratis or berbayar
    scoring_type: 'standard', // standard (B=5,S=0) or weighted (0-5)
    // Custom scoring values
    score_correct: 5,    // Nilai untuk jawaban benar
    score_wrong: 0,      // Nilai untuk jawaban salah  
    score_blank: 0,      // Nilai untuk tidak menjawab
    options: [
      { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 },
      { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 },
      { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 },
      { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 },
    ]
  });
  const [showFullPreview, setShowFullPreview] = useState(false);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Get mapel options from subjects database, fallback to hardcoded if no subjects exist
  const mapelOptions = useMemo(() => {
    if (!formData.program) return [];
    const fromDB = subjects.filter(s => s.program === formData.program && s.is_active !== false).map(s => s.name);
    return fromDB.length > 0 ? fromDB : (MAPEL_OPTIONS[formData.program] || []);
  }, [formData.program, subjects]);
  
  // Get kategori options from categories database, fallback to generated if no categories exist
  const kategoriOptions = useMemo(() => {
    if (!formData.mapel || !formData.program) return [];
    const fromDB = categories.filter(c => c.program === formData.program && c.subject === formData.mapel && c.is_active !== false).map(c => c.name);
    return fromDB.length > 0 ? fromDB : generateKategori(formData.mapel);
  }, [formData.program, formData.mapel, categories]);

  // Question type configuration
  const QUESTION_TYPE_CONFIG = {
    multiple_choice: { 
      name: 'Pilihan Ganda', 
      hasCorrectAnswer: true, 
      supportsWeight: false,
      description: 'Soal dengan satu jawaban benar (B=5, S=0)'
    },
    multiple_choice_weighted: { 
      name: 'Pilihan Ganda Berbobot (TKP)', 
      hasCorrectAnswer: false, 
      supportsWeight: true,
      description: 'Setiap opsi memiliki bobot nilai 0-5 (untuk TKP)'
    },
    multiple_choice_custom: { 
      name: 'Pilihan Ganda Custom', 
      hasCorrectAnswer: true, 
      supportsWeight: false,
      supportsCustomScoring: true,
      description: 'Soal dengan skor custom (atur nilai Benar/Salah/Kosong)'
    },
    likert: { 
      name: 'Skala Likert', 
      hasCorrectAnswer: false, 
      supportsWeight: true,
      description: 'Skala dari Sangat Tidak Setuju sampai Sangat Setuju'
    },
    yes_no: { 
      name: 'Ya/Tidak', 
      hasCorrectAnswer: true, 
      supportsWeight: false,
      description: 'Soal dengan jawaban Ya atau Tidak'
    },
    ab: { 
      name: 'Pilihan A/B', 
      hasCorrectAnswer: false, 
      supportsWeight: true,
      description: 'Pilihan antara dua opsi A dan B'
    }
  };

  // Generate default options based on question type
  const generateDefaultOptions = (questionType, likertScale = 5) => {
    switch (questionType) {
      case 'yes_no':
        // Admin pilih mana yang benar, default keduanya belum dipilih
        return [
          { id: crypto.randomUUID(), text: 'Ya', is_correct: false, weight: 0 },
          { id: crypto.randomUUID(), text: 'Tidak', is_correct: false, weight: 0 }
        ];
      case 'ab':
        return [
          { id: crypto.randomUUID(), text: 'A', is_correct: false, weight: 0 },
          { id: crypto.randomUUID(), text: 'B', is_correct: false, weight: 0 }
        ];
      case 'likert':
        if (likertScale === 7) {
          return [
            { id: crypto.randomUUID(), text: 'Sangat Tidak Setuju', is_correct: false, weight: 1 },
            { id: crypto.randomUUID(), text: 'Tidak Setuju', is_correct: false, weight: 2 },
            { id: crypto.randomUUID(), text: 'Agak Tidak Setuju', is_correct: false, weight: 3 },
            { id: crypto.randomUUID(), text: 'Netral', is_correct: false, weight: 4 },
            { id: crypto.randomUUID(), text: 'Agak Setuju', is_correct: false, weight: 5 },
            { id: crypto.randomUUID(), text: 'Setuju', is_correct: false, weight: 6 },
            { id: crypto.randomUUID(), text: 'Sangat Setuju', is_correct: false, weight: 7 }
          ];
        }
        return [
          { id: crypto.randomUUID(), text: 'Sangat Tidak Setuju', is_correct: false, weight: 1 },
          { id: crypto.randomUUID(), text: 'Tidak Setuju', is_correct: false, weight: 2 },
          { id: crypto.randomUUID(), text: 'Netral', is_correct: false, weight: 3 },
          { id: crypto.randomUUID(), text: 'Setuju', is_correct: false, weight: 4 },
          { id: crypto.randomUUID(), text: 'Sangat Setuju', is_correct: false, weight: 5 }
        ];
      case 'multiple_choice_weighted':
        // TKP questions have weighted options, no single correct answer
        return [
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 5 },
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 4 },
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 3 },
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 2 },
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 1 }
        ];
      default: // multiple_choice, multiple_choice_custom
        return [
          { id: crypto.randomUUID(), text: '', is_correct: true, weight: 0 },
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 },
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 },
          { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 }
        ];
    }
  };

  useEffect(() => {
    if (data) {
      setFormData({
        program: data.program || data.category || '',
        mapel: data.mapel || '',
        kategori: data.kategori || '',
        question_type: data.question_type || 'multiple_choice',
        difficulty: data.difficulty || 'medium',
        time_limit: String(data.time_limit || 60),
        text: data.text || '',
        explanation: data.explanation || '',
        points: data.points || 10,
        likert_scale: data.likert_scale || 5,
        access_type: data.access_type || 'gratis',
        scoring_type: data.scoring_type || 'standard',
        score_correct: data.score_correct ?? 5,
        score_wrong: data.score_wrong ?? 0,
        score_blank: data.score_blank ?? 0,
        options: data.options?.map(o => ({...o, weight: o.weight || 0})) || generateDefaultOptions(data.question_type || 'multiple_choice'),
        assign_to_tryout: ''
      });
    } else {
      setFormData({
        program: '',
        mapel: '',
        kategori: '',
        question_type: 'multiple_choice',
        difficulty: 'medium',
        time_limit: '60',
        text: '',
        explanation: '',
        points: 10,
        likert_scale: 5,
        access_type: 'gratis',
        scoring_type: 'standard',
        score_correct: 5,
        score_wrong: 0,
        score_blank: 0,
        options: generateDefaultOptions('multiple_choice'),
        assign_to_tryout: ''
      });
    }
  }, [data, open]);

  // Handle question type change
  const handleQuestionTypeChange = (newType) => {
    const newOptions = generateDefaultOptions(newType, formData.likert_scale);
    setFormData({ 
      ...formData, 
      question_type: newType, 
      options: newOptions 
    });
  };

  // Handle likert scale change
  const handleLikertScaleChange = (scale) => {
    const newOptions = generateDefaultOptions('likert', parseInt(scale));
    setFormData({
      ...formData,
      likert_scale: parseInt(scale),
      options: newOptions
    });
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    const config = QUESTION_TYPE_CONFIG[formData.question_type];
    
    if (field === 'is_correct' && value && config.hasCorrectAnswer) {
      // For question types with correct answer, only one can be correct
      newOptions.forEach((opt, i) => {
        opt.is_correct = i === index;
      });
    } else if (field === 'weight') {
      // Ensure weight is between 0-5
      const weightValue = Math.min(5, Math.max(0, parseInt(value) || 0));
      newOptions[index] = { ...newOptions[index], weight: weightValue };
    } else {
      newOptions[index] = { ...newOptions[index], [field]: value };
    }
    setFormData({ ...formData, options: newOptions });
  };

  const addOption = () => {
    if (formData.question_type === 'yes_no' || formData.question_type === 'ab' || formData.question_type === 'likert') {
      return; // These types have fixed options
    }
    setFormData({
      ...formData,
      options: [...formData.options, { id: crypto.randomUUID(), text: '', is_correct: false, weight: 0 }]
    });
  };

  const removeOption = (index) => {
    if (formData.question_type === 'yes_no' || formData.question_type === 'ab' || formData.question_type === 'likert') {
      return; // These types have fixed options
    }
    if (formData.options.length <= 2) return;
    const newOptions = formData.options.filter((_, i) => i !== index);
    // If removed option was correct, make first one correct
    if (!newOptions.some(o => o.is_correct) && QUESTION_TYPE_CONFIG[formData.question_type].hasCorrectAnswer) {
      newOptions[0].is_correct = true;
    }
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      text: formData.text,
      question_type: formData.question_type,
      program: formData.program,
      mapel: formData.mapel,
      kategori: formData.kategori,
      category: formData.program, // backward compatible
      difficulty: formData.difficulty,
      time_limit: parseInt(formData.time_limit),
      points: formData.points,
      explanation: formData.explanation,
      options: formData.options,
      likert_scale: formData.likert_scale,
      assign_to_tryout: formData.assign_to_tryout || null,
      access_type: formData.access_type,
      // Custom scoring for multiple_choice_custom type
      score_correct: formData.question_type === 'multiple_choice_custom' ? formData.score_correct : 5,
      score_wrong: formData.question_type === 'multiple_choice_custom' ? formData.score_wrong : 0,
      score_blank: formData.question_type === 'multiple_choice_custom' ? formData.score_blank : 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Soal Baru' : 'Edit Soal'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Row 1: Program, Mapel, Kategori */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Program <span className="text-red-500">*</span></Label>
              <Select 
                value={formData.program} 
                onValueChange={(v) => setFormData({ ...formData, program: v, mapel: '', kategori: '' })}
              >
                <SelectTrigger data-testid="question-program-select">
                  <SelectValue placeholder="Pilih Program" />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map(prog => (
                    <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Mata Pelajaran <span className="text-red-500">*</span></Label>
              <Select 
                value={formData.mapel} 
                onValueChange={(v) => setFormData({ ...formData, mapel: v, kategori: '' })}
                disabled={!formData.program}
              >
                <SelectTrigger data-testid="question-mapel-select">
                  <SelectValue placeholder="Pilih Mapel" />
                </SelectTrigger>
                <SelectContent>
                  {mapelOptions.map(mapel => (
                    <SelectItem key={mapel} value={mapel}>{mapel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Kategori</Label>
              <Select 
                value={formData.kategori} 
                onValueChange={(v) => setFormData({ ...formData, kategori: v })}
                disabled={!formData.mapel}
              >
                <SelectTrigger data-testid="question-kategori-select">
                  <SelectValue placeholder="Pilih Kategori" />
                </SelectTrigger>
                <SelectContent>
                  {kategoriOptions.map(kat => (
                    <SelectItem key={kat} value={kat}>{kat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Tipe Akses</Label>
              <Select 
                value={formData.access_type} 
                onValueChange={(v) => setFormData({ ...formData, access_type: v })}
              >
                <SelectTrigger data-testid="question-access-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gratis">🟢 Gratis</SelectItem>
                  <SelectItem value="berbayar">🟡 Berbayar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Assign ke Try Out</Label>
              <Select 
                value={formData.assign_to_tryout} 
                onValueChange={(v) => setFormData({ ...formData, assign_to_tryout: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="question-tryout-select">
                  <SelectValue placeholder="Pilih Try Out (opsional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Tidak assign --</SelectItem>
                  {tryouts
                    .filter(t => !formData.program || t.category === formData.program)
                    .map(tryout => (
                      <SelectItem key={tryout.id} value={tryout.id}>
                        {tryout.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Tipe Soal, Tingkat Kesulitan, Batas Waktu */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Tipe Soal</Label>
              <Select value={formData.question_type} onValueChange={handleQuestionTypeChange}>
                <SelectTrigger data-testid="question-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="multiple_choice">Pilihan Ganda (B=5, S=0)</SelectItem>
                  <SelectItem value="multiple_choice_custom">Pilihan Ganda Custom (Atur Skor)</SelectItem>
                  <SelectItem value="multiple_choice_weighted">Pilihan Ganda Berbobot/TKP (0-5)</SelectItem>
                  <SelectItem value="likert">Skala Likert</SelectItem>
                  <SelectItem value="yes_no">Ya/Tidak</SelectItem>
                  <SelectItem value="ab">Pilihan A/B</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {QUESTION_TYPE_CONFIG[formData.question_type]?.description}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Tingkat Kesulitan</Label>
              <Select value={formData.difficulty} onValueChange={(v) => setFormData({ ...formData, difficulty: v })}>
                <SelectTrigger data-testid="question-difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Mudah</SelectItem>
                  <SelectItem value="medium">Sedang</SelectItem>
                  <SelectItem value="hard">Sulit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" /> Batas Waktu
              </Label>
              <Select value={formData.time_limit} onValueChange={(v) => setFormData({ ...formData, time_limit: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_LIMITS.map(tl => (
                    <SelectItem key={tl.value} value={tl.value}>{tl.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Likert Scale Option - Only show for Likert type */}
          {formData.question_type === 'likert' && (
            <div className="space-y-2">
              <Label className="text-sm">Skala Likert</Label>
              <Select value={String(formData.likert_scale)} onValueChange={handleLikertScaleChange}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 Poin (STS - SS)</SelectItem>
                  <SelectItem value="7">7 Poin (Lebih Detail)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Custom Scoring Options - Only show for multiple_choice_custom type */}
          {formData.question_type === 'multiple_choice_custom' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
                <Sparkles className="w-4 h-4" />
                Pengaturan Skor Custom
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-amber-700">Nilai Benar</Label>
                  <Input
                    type="number"
                    value={formData.score_correct}
                    onChange={(e) => setFormData({ ...formData, score_correct: parseFloat(e.target.value) || 0 })}
                    className="h-8 text-sm bg-white"
                    min="-100"
                    max="100"
                    step="0.5"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-amber-700">Nilai Salah</Label>
                  <Input
                    type="number"
                    value={formData.score_wrong}
                    onChange={(e) => setFormData({ ...formData, score_wrong: parseFloat(e.target.value) || 0 })}
                    className="h-8 text-sm bg-white"
                    min="-100"
                    max="100"
                    step="0.5"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-amber-700">Nilai Kosong</Label>
                  <Input
                    type="number"
                    value={formData.score_blank}
                    onChange={(e) => setFormData({ ...formData, score_blank: parseFloat(e.target.value) || 0 })}
                    className="h-8 text-sm bg-white"
                    min="-100"
                    max="100"
                    step="0.5"
                  />
                </div>
              </div>
              <p className="text-xs text-amber-600">
                Contoh preset: CPNS (B=5, S=0, K=0) | UTBK (B=4, S=-1, K=0) | Custom sesuai kebutuhan
              </p>
              {/* Quick Preset Buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFormData({ ...formData, score_correct: 5, score_wrong: 0, score_blank: 0 })}
                >
                  CPNS (5/0/0)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFormData({ ...formData, score_correct: 4, score_wrong: -1, score_blank: 0 })}
                >
                  UTBK (4/-1/0)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFormData({ ...formData, score_correct: 4, score_wrong: 0, score_blank: 1 })}
                >
                  TPA (4/0/1)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFormData({ ...formData, score_correct: 1, score_wrong: 0, score_blank: 0 })}
                >
                  Standar (1/0/0)
                </Button>
              </div>
            </div>
          )}

          {/* Teks Soal dengan Rich Text Editor */}
          <div className="space-y-2">
            <Label className="text-sm">
              Teks Soal <span className="text-red-500">*</span>
            </Label>
            <RichTextEditor
              value={formData.text}
              onChange={(value) => setFormData({ ...formData, text: value })}
              placeholder="Ketik soal di sini..."
              apiUrl={apiUrl}
              minHeight={300}
            />
          </div>

          {/* Full Question Preview */}
          {showFullPreview && (
            <QuestionPreview 
              question={formData.text} 
              options={formData.options} 
              explanation={formData.explanation}
            />
          )}

          {/* Pilihan Jawaban */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Pilihan Jawaban</Label>
                {QUESTION_TYPE_CONFIG[formData.question_type]?.supportsWeight && (
                  <p className="text-xs text-blue-600 mt-1">
                    Setiap opsi memiliki bobot nilai (0-5). Semakin tinggi bobot, semakin tinggi nilai.
                  </p>
                )}
              </div>
              {!['yes_no', 'ab', 'likert'].includes(formData.question_type) && (
                <Button type="button" variant="outline" size="sm" onClick={addOption}>
                  <Plus className="w-4 h-4 mr-1" />
                  Tambah Opsi
                </Button>
              )}
            </div>
            {formData.options.map((opt, i) => (
              <div key={opt.id} className="flex items-start gap-3">
                {/* Option Letter/Selector */}
                {QUESTION_TYPE_CONFIG[formData.question_type]?.hasCorrectAnswer ? (
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 cursor-pointer transition-colors mt-1 ${
                      opt.is_correct ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                    onClick={() => handleOptionChange(i, 'is_correct', true)}
                    title={opt.is_correct ? 'Jawaban Benar' : 'Klik untuk set sebagai jawaban benar'}
                  >
                    {String.fromCharCode(65 + i)}
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 bg-slate-200 text-slate-600 mt-1">
                    {String.fromCharCode(65 + i)}
                  </div>
                )}
                
                {/* Option Text with Mini Rich Text Editor */}
                <div className="flex-1">
                  {['yes_no', 'ab', 'likert'].includes(formData.question_type) ? (
                    <Input
                      value={opt.text}
                      onChange={(e) => handleOptionChange(i, 'text', e.target.value)}
                      placeholder={`Pilihan ${String.fromCharCode(65 + i)}`}
                      required
                      disabled
                      data-testid={`option-input-${i}`}
                    />
                  ) : (
                    <MiniRichTextEditor
                      value={opt.text}
                      onChange={(value) => handleOptionChange(i, 'text', value)}
                      placeholder={`Pilihan ${String.fromCharCode(65 + i)} - Ketik teks, gambar, atau rumus...`}
                      disabled={false}
                      apiUrl={apiUrl}
                    />
                  )}
                </div>
                
                {/* Weight Input - Only for weighted types */}
                {QUESTION_TYPE_CONFIG[formData.question_type]?.supportsWeight && (
                  <div className="flex items-center gap-2 mt-1">
                    <Label className="text-xs text-slate-500 whitespace-nowrap">Bobot:</Label>
                    <Input
                      type="number"
                      min="0"
                      max="5"
                      value={opt.weight}
                      onChange={(e) => handleOptionChange(i, 'weight', e.target.value)}
                      className="w-16 text-center"
                      data-testid={`option-weight-${i}`}
                    />
                  </div>
                )}
                
                {/* Delete Button - Only for customizable types */}
                {!['yes_no', 'ab', 'likert'].includes(formData.question_type) && formData.options.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
                    onClick={() => removeOption(i)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {QUESTION_TYPE_CONFIG[formData.question_type]?.hasCorrectAnswer && (
              <p className="text-xs text-slate-500">Klik huruf (A, B, C, D) untuk menandai jawaban yang benar</p>
            )}
            {formData.question_type === 'multiple_choice_weighted' && (
              <p className="text-xs text-amber-600">
                Tips: Untuk TKP CPNS, gunakan bobot 5 untuk jawaban paling ideal, 4 untuk baik, 3 untuk cukup, dst.
              </p>
            )}
          </div>

          {/* Pembahasan dengan Rich Text Editor */}
          <div className="space-y-2">
            <Label className="text-sm">Pembahasan (Opsional)</Label>
            <RichTextEditor
              value={formData.explanation}
              onChange={(value) => setFormData({ ...formData, explanation: value })}
              placeholder="Tulis pembahasan jawaban di sini... (bisa tambah gambar)"
              apiUrl={apiUrl}
              minHeight={150}
            />
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowFullPreview(!showFullPreview)}
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {showFullPreview ? 'Sembunyikan Preview' : 'Preview Soal'}
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
              <Button type="submit" className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="save-question-btn">
                {mode === 'create' ? 'Tambah Soal' : 'Simpan Perubahan'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// TryOut Dialog Component
// Scoring presets
const SCORING_PRESETS = {
  "standard": { correct: 1, wrong: 0, blank: 0, description: "Standar: Benar +1, Salah 0" },
  "cpns": { correct: 5, wrong: 0, blank: 0, description: "CPNS: Benar +5, Salah 0" },
  "utbk_irt": { correct: 1, wrong: 0, blank: 0, use_irt: true, description: "UTBK IRT (bobot sesuai kesulitan)" },
  "negative_1": { correct: 4, wrong: -1, blank: 0, description: "Benar +4, Salah -1" },
  "negative_2": { correct: 3, wrong: -1, blank: 0, description: "Benar +3, Salah -1" },
  "no_penalty": { correct: 2, wrong: 0, blank: 0, description: "Benar +2, Salah 0" },
  "custom": { correct: 1, wrong: 0, blank: 0, description: "Kustom" }
};

function TryoutDialog({ open, mode, data, onClose, onSave, subjects = [] }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    duration_minutes: 60,
    time_limits: [], // Per-mapel time limits
    price: 0,
    is_active: true,
    scoring_preset: 'standard',
    scoring_config: {
      correct_points: 1,
      wrong_points: 0,
      blank_points: 0,
      use_irt: false
    }
  });

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        description: data.description || '',
        category: data.category || '',
        duration_minutes: data.duration_minutes || 60,
        time_limits: data.time_limits || [],
        price: data.price || 0,
        is_active: data.is_active !== false,
        scoring_preset: data.scoring_preset || 'standard',
        scoring_config: data.scoring_config || {
          correct_points: 1,
          wrong_points: 0,
          blank_points: 0,
          use_irt: false
        }
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: PROGRAMS[0] || '',
        duration_minutes: 60,
        time_limits: [],
        price: 0,
        is_active: true,
        scoring_preset: 'standard',
        scoring_config: {
          correct_points: 1,
          wrong_points: 0,
          blank_points: 0,
          use_irt: false
        }
      });
    }
  }, [data, open]);

  // Auto-update scoring config when preset changes
  const handlePresetChange = (preset) => {
    if (preset !== 'custom' && SCORING_PRESETS[preset]) {
      const p = SCORING_PRESETS[preset];
      setFormData({
        ...formData,
        scoring_preset: preset,
        scoring_config: {
          correct_points: p.correct,
          wrong_points: p.wrong,
          blank_points: p.blank,
          use_irt: p.use_irt || false
        }
      });
    } else {
      setFormData({ ...formData, scoring_preset: preset });
    }
  };

  // Get mapel options for selected category - use subjects from database first, fallback to hardcoded
  const mapelOptions = useMemo(() => {
    if (!formData.category) return [];
    const fromDB = subjects.filter(s => s.program === formData.category && s.is_active !== false).map(s => s.name);
    return fromDB.length > 0 ? fromDB : (MAPEL_OPTIONS[formData.category] || []);
  }, [formData.category, subjects]);

  // Add time limit for a mapel
  const addTimeLimit = () => {
    if (mapelOptions.length > 0) {
      const firstAvailable = mapelOptions.find(m => !formData.time_limits.some(tl => tl.mapel === m));
      if (firstAvailable) {
        setFormData({
          ...formData,
          time_limits: [...formData.time_limits, { mapel: firstAvailable, duration_minutes: 30 }]
        });
      }
    }
  };

  // Remove time limit
  const removeTimeLimit = (index) => {
    setFormData({
      ...formData,
      time_limits: formData.time_limits.filter((_, i) => i !== index)
    });
  };

  // Update time limit
  const updateTimeLimit = (index, field, value) => {
    const newTimeLimits = [...formData.time_limits];
    newTimeLimits[index] = { ...newTimeLimits[index], [field]: value };
    setFormData({ ...formData, time_limits: newTimeLimits });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Try Out Baru' : 'Edit Try Out'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Judul</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Judul try out"
              required
              data-testid="tryout-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi try out"
              data-testid="tryout-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Program</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v, time_limits: [] })}
              >
                <SelectTrigger data-testid="tryout-category-select">
                  <SelectValue placeholder="Pilih Program" />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map(prog => (
                    <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Durasi Total (menit)</Label>
              <Input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                min={1}
                data-testid="tryout-duration-input"
              />
              <p className="text-xs text-slate-500">Fallback jika tidak ada batas waktu per mapel</p>
            </div>
          </div>

          {/* Time Limits per Mapel */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Batas Waktu per Mapel
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTimeLimit}
                disabled={formData.time_limits.length >= mapelOptions.length}
                data-testid="add-time-limit-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Tambah
              </Button>
            </div>
            
            {formData.time_limits.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4 border border-dashed border-slate-200 rounded-lg">
                Belum ada batas waktu per mapel.
                <br />
                Durasi total akan digunakan untuk semua soal.
              </div>
            ) : (
              <div className="space-y-2">
                {formData.time_limits.map((tl, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Select
                      value={tl.mapel}
                      onValueChange={(v) => updateTimeLimit(index, 'mapel', v)}
                    >
                      <SelectTrigger className="w-40" data-testid={`time-limit-mapel-${index}`}>
                        <SelectValue placeholder="Pilih Mapel" />
                      </SelectTrigger>
                      <SelectContent>
                        {mapelOptions.map(m => (
                          <SelectItem 
                            key={m} 
                            value={m}
                            disabled={formData.time_limits.some((t, i) => i !== index && t.mapel === m)}
                          >
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={tl.duration_minutes}
                      onChange={(e) => updateTimeLimit(index, 'duration_minutes', parseInt(e.target.value) || 1)}
                      min={1}
                      className="w-20"
                      data-testid={`time-limit-duration-${index}`}
                    />
                    <span className="text-sm text-slate-500">menit</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTimeLimit(index)}
                      className="ml-auto text-red-500 hover:text-red-700"
                      data-testid={`remove-time-limit-${index}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Harga (Rp)</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                min={0}
                data-testid="tryout-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex items-center gap-2 h-10">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                  data-testid="tryout-active-switch"
                />
                <span>{formData.is_active ? 'Aktif' : 'Nonaktif'}</span>
              </div>
            </div>
          </div>

          {/* Scoring Configuration */}
          <div className="space-y-3 border-t pt-4">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <Trophy className="w-4 h-4 text-amber-500" />
              Sistem Penilaian
            </Label>
            
            <div className="space-y-2">
              <Label className="text-sm">Pilih Rumus Penilaian</Label>
              <Select 
                value={formData.scoring_preset} 
                onValueChange={handlePresetChange}
              >
                <SelectTrigger data-testid="scoring-preset-select">
                  <SelectValue placeholder="Pilih rumus penilaian" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SCORING_PRESETS).map(([key, preset]) => (
                    <SelectItem key={key} value={key}>
                      {preset.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom scoring inputs */}
            {formData.scoring_preset === 'custom' && (
              <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-xs">Benar (+)</Label>
                  <Input
                    type="number"
                    value={formData.scoring_config.correct_points}
                    onChange={(e) => setFormData({
                      ...formData,
                      scoring_config: { ...formData.scoring_config, correct_points: parseFloat(e.target.value) || 0 }
                    })}
                    step="0.5"
                    className="h-8"
                    data-testid="scoring-correct-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Salah (-)</Label>
                  <Input
                    type="number"
                    value={formData.scoring_config.wrong_points}
                    onChange={(e) => setFormData({
                      ...formData,
                      scoring_config: { ...formData.scoring_config, wrong_points: parseFloat(e.target.value) || 0 }
                    })}
                    step="0.5"
                    className="h-8"
                    data-testid="scoring-wrong-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Kosong</Label>
                  <Input
                    type="number"
                    value={formData.scoring_config.blank_points}
                    onChange={(e) => setFormData({
                      ...formData,
                      scoring_config: { ...formData.scoring_config, blank_points: parseFloat(e.target.value) || 0 }
                    })}
                    step="0.5"
                    className="h-8"
                    data-testid="scoring-blank-input"
                  />
                </div>
              </div>
            )}
            
            {/* IRT Info */}
            {formData.scoring_config.use_irt && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-1">Item Response Theory (IRT)</p>
                <p className="text-blue-600 text-xs">
                  Bobot nilai berdasarkan tingkat kesulitan soal:
                  <br />• Soal Mudah: 1x poin
                  <br />• Soal Sedang: 1.5x poin  
                  <br />• Soal Sulit: 2x poin
                </p>
              </div>
            )}
            
            {/* Preview current scoring */}
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Preview:</span>
              <Badge variant="outline" className="bg-green-50 text-green-700">
                Benar +{formData.scoring_config.correct_points}
              </Badge>
              <Badge variant="outline" className={formData.scoring_config.wrong_points < 0 ? "bg-red-50 text-red-700" : "bg-slate-50"}>
                Salah {formData.scoring_config.wrong_points >= 0 ? '+' : ''}{formData.scoring_config.wrong_points}
              </Badge>
              <Badge variant="outline" className="bg-slate-50">
                Kosong {formData.scoring_config.blank_points >= 0 ? '+' : ''}{formData.scoring_config.blank_points}
              </Badge>
              {formData.scoring_config.use_irt && (
                <Badge className="bg-blue-100 text-blue-700">IRT</Badge>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="save-tryout-btn">
              {mode === 'create' ? 'Tambah Try Out' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Package Dialog Component
function PackageDialog({ open, mode, data, onClose, onSave, tryouts = [] }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    program: '',
    package_type: 'tryout',
    price: 0,
    discount_percent: 0,
    is_active: true,
    features: [],
    tryout_ids: [],
    tier: 'basic'
  });
  const [featureInput, setFeatureInput] = useState('');
  const [searchTryout, setSearchTryout] = useState('');

  // Filter tryouts by selected program (tryouts use 'category' field, not 'program')
  const filteredTryouts = tryouts.filter(t => {
    const matchProgram = !formData.program || t.category === formData.program;
    const matchSearch = !searchTryout || t.title.toLowerCase().includes(searchTryout.toLowerCase());
    return matchProgram && matchSearch;
  });

  // Tier presets with pricing
  const TIER_PRESETS = {
    basic: { label: 'Basic', count: 5, price: 50000, color: 'bg-slate-100 border-slate-300 text-slate-700' },
    silver: { label: 'Silver', count: 10, price: 100000, color: 'bg-slate-200 border-slate-400 text-slate-700' },
    gold: { label: 'Gold', count: 15, price: 150000, color: 'bg-amber-100 border-amber-400 text-amber-700' },
    platinum: { label: 'Platinum', count: 20, price: 200000, color: 'bg-purple-100 border-purple-400 text-purple-700' }
  };

  // Auto-set price when tier changes
  const handleTierChange = (tier) => {
    const preset = TIER_PRESETS[tier];
    setFormData({ 
      ...formData, 
      tier: tier,
      price: preset?.price || 0
    });
  };

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        description: data.description || '',
        program: data.program || '',
        package_type: data.package_type || 'tryout',
        price: data.price || 0,
        discount_percent: data.discount_percent || 0,
        is_active: data.is_active !== false,
        features: data.features || [],
        tryout_ids: data.tryout_ids || [],
        tier: data.tier || 'basic'
      });
    } else {
      setFormData({
        title: '',
        description: '',
        program: '',
        package_type: 'tryout',
        price: 0,
        discount_percent: 0,
        is_active: true,
        features: [],
        tryout_ids: [],
        tier: 'basic'
      });
    }
    setFeatureInput('');
    setSearchTryout('');
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({ ...formData, features: [...formData.features, featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Paket Baru' : 'Edit Paket'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Judul Paket</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Contoh: Paket Silver UTBK-SNBT"
              required
              data-testid="package-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Program *</Label>
              <Select 
                value={formData.program} 
                onValueChange={(v) => setFormData({ ...formData, program: v })}
              >
                <SelectTrigger data-testid="package-program-select">
                  <SelectValue placeholder="Pilih Program" />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAM_OPTIONS.map(prog => (
                    <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipe Paket</Label>
              <Select 
                value={formData.package_type} 
                onValueChange={(v) => setFormData({ ...formData, package_type: v })}
              >
                <SelectTrigger data-testid="package-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tryout">Try Out</SelectItem>
                  <SelectItem value="zoom">Kelas Zoom</SelectItem>
                  <SelectItem value="ebook">eBook</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi singkat paket"
              data-testid="package-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Harga (Rp) <span className="text-xs text-slate-400">- bisa di-edit manual</span></Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                min={0}
                data-testid="package-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Diskon (%) <span className="text-xs text-slate-400">- opsional</span></Label>
              <Input
                type="number"
                value={formData.discount_percent}
                onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) || 0 })}
                min={0}
                max={100}
                data-testid="package-discount-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Fitur/Keunggulan</Label>
            <div className="flex gap-2">
              <Input
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                placeholder="Contoh: Akses 10x Try Out"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                data-testid="feature-input"
              />
              <Button type="button" variant="outline" onClick={addFeature} data-testid="add-feature-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.features.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.features.map((f, i) => (
                  <Badge key={i} variant="secondary" className="pr-1">
                    {f}
                    <button 
                      type="button" 
                      className="ml-1 hover:text-red-500"
                      onClick={() => removeFeature(i)}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                data-testid="package-active-switch"
              />
              <span>{formData.is_active ? 'Aktif' : 'Nonaktif'}</span>
            </div>
          </div>

          {/* Tier Selection for Tryout packages */}
          {formData.package_type === 'tryout' && (
            <div className="space-y-2">
              <Label>Tier Paket (Auto-set harga)</Label>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(TIER_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleTierChange(key)}
                    className={`p-2 rounded-lg border-2 text-center transition-all ${preset.color} ${
                      formData.tier === key ? 'ring-2 ring-[#4472C4] ring-offset-1' : ''
                    }`}
                  >
                    <div className="font-semibold text-xs">{preset.label}</div>
                    <div className="text-[10px] text-slate-500">{preset.count} TO</div>
                    <div className="text-xs font-bold mt-0.5">Rp {(preset.price/1000)}K</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Try Out Selection for Tryout packages */}
          {formData.package_type === 'tryout' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Pilih Try Out ({formData.tryout_ids.length} dipilih)</Label>
                <span className="text-xs text-slate-500">
                  Rekomendasi: {TIER_PRESETS[formData.tier]?.count || 5} Try Out
                </span>
              </div>
              
              <Input
                placeholder="Cari Try Out..."
                value={searchTryout}
                onChange={(e) => setSearchTryout(e.target.value)}
                className="mb-2"
              />
              
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {filteredTryouts.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    {formData.program ? 'Tidak ada Try Out untuk program ini' : 'Pilih program terlebih dahulu'}
                  </div>
                ) : (
                  filteredTryouts.map((tryout) => {
                    const isSelected = formData.tryout_ids.includes(tryout.id);
                    return (
                      <div
                        key={tryout.id}
                        onClick={() => {
                          if (isSelected) {
                            setFormData({
                              ...formData,
                              tryout_ids: formData.tryout_ids.filter(id => id !== tryout.id)
                            });
                          } else {
                            setFormData({
                              ...formData,
                              tryout_ids: [...formData.tryout_ids, tryout.id]
                            });
                          }
                        }}
                        className={`p-3 border-b last:border-b-0 cursor-pointer flex items-center gap-3 hover:bg-slate-50 ${
                          isSelected ? 'bg-[#4472C4]/10' : ''
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-[#4472C4] border-[#4472C4]' : 'border-slate-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{tryout.title}</div>
                          <div className="text-xs text-slate-500">
                            {tryout.total_questions || 0} soal • {tryout.duration || 0} menit
                            {tryout.price > 0 ? ` • Rp ${tryout.price.toLocaleString()}` : ' • Gratis'}
                          </div>
                        </div>
                        <Badge variant={tryout.is_active ? 'default' : 'secondary'} className="text-xs">
                          {tryout.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </div>
                    );
                  })
                )}
              </div>
              
              {formData.tryout_ids.length > 0 && (
                <div className="flex items-center justify-between text-sm mt-2 p-2 bg-slate-50 rounded">
                  <span className="text-slate-600">{formData.tryout_ids.length} Try Out terpilih</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setFormData({ ...formData, tryout_ids: [] })}
                    className="text-red-500 hover:text-red-700"
                  >
                    Hapus Semua
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="save-package-btn">
              {mode === 'create' ? 'Tambah Paket' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Selection Dialog Component
function SelectionDialog({ open, title, items, selectedIds, onClose, onSave, onSelectionChange, renderItem }) {
  const [search, setSearch] = useState('');

  const filteredItems = items.filter(item => {
    const searchText = item.text || item.title || '';
    return searchText.toLowerCase().includes(search.toLowerCase());
  });

  const toggleItem = (id) => {
    const newIds = selectedIds.includes(id)
      ? selectedIds.filter(i => i !== id)
      : [...selectedIds, id];
    onSelectionChange(newIds);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl flex flex-col" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="shrink-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Pilih item yang ingin ditambahkan ({selectedIds.length} dipilih)</DialogDescription>
        </DialogHeader>

        <div className="relative shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Cari..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 min-h-0" style={{ maxHeight: '400px' }}>
          {filteredItems.map((item) => (
            <div
              key={item.id}
              onClick={() => toggleItem(item.id)}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                selectedIds.includes(item.id)
                  ? 'border-[#4472C4] bg-[#4472C4]/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={selectedIds.includes(item.id)}
                  className="mt-1"
                />
                <div className="flex-1">
                  {renderItem(item)}
                </div>
              </div>
            </div>
          ))}
          {filteredItems.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              Tidak ada item yang ditemukan
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t pt-4 mt-2">
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={onSave} className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="save-selection-btn">
            Simpan ({selectedIds.length} item)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Promo Dialog Component
function PromoDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    discount_percent: 0,
    discount_amount: 0,
    min_purchase: 0,
    max_discount: 0,
    start_date: '',
    end_date: '',
    is_active: true,
    usage_limit: 0
  });

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        title: data.title || '',
        description: data.description || '',
        discount_percent: data.discount_percent || 0,
        discount_amount: data.discount_amount || 0,
        min_purchase: data.min_purchase || 0,
        max_discount: data.max_discount || 0,
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        is_active: data.is_active !== false,
        usage_limit: data.usage_limit || 0
      });
    } else {
      setFormData({
        code: '',
        title: '',
        description: '',
        discount_percent: 0,
        discount_amount: 0,
        min_purchase: 0,
        max_discount: 0,
        start_date: '',
        end_date: '',
        is_active: true,
        usage_limit: 0
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Promo Baru' : 'Edit Promo'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kode Promo</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="PROMO2025"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Judul</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Diskon Akhir Tahun"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi promo"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Diskon (%)</Label>
              <Input
                type="number"
                value={formData.discount_percent}
                onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) || 0 })}
                min={0}
                max={100}
              />
            </div>
            <div className="space-y-2">
              <Label>Diskon (Rp)</Label>
              <Input
                type="number"
                value={formData.discount_amount}
                onChange={(e) => setFormData({ ...formData, discount_amount: parseInt(e.target.value) || 0 })}
                min={0}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Min. Pembelian (Rp)</Label>
              <Input
                type="number"
                value={formData.min_purchase}
                onChange={(e) => setFormData({ ...formData, min_purchase: parseInt(e.target.value) || 0 })}
                min={0}
              />
            </div>
            <div className="space-y-2">
              <Label>Batas Penggunaan</Label>
              <Input
                type="number"
                value={formData.usage_limit}
                onChange={(e) => setFormData({ ...formData, usage_limit: parseInt(e.target.value) || 0 })}
                min={0}
                placeholder="0 = unlimited"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tanggal Mulai</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Tanggal Berakhir</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
            />
            <span>Aktif</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" className="bg-[#4472C4] hover:bg-[#3861a8]">
              {mode === 'create' ? 'Tambah Promo' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Zoom Class Dialog Component
function ZoomClassDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    program: '',
    mapel: '',
    instructor: '',
    zoom_link: '',
    schedule_date: '',
    schedule_time: '',
    duration_minutes: 60,
    max_participants: 100,
    is_active: true
  });

  const mapelOptions = formData.program ? MAPEL_OPTIONS[formData.program] || [] : [];

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        description: data.description || '',
        program: data.program || '',
        mapel: data.mapel || '',
        instructor: data.instructor || '',
        zoom_link: data.zoom_link || '',
        schedule_date: data.schedule_date || '',
        schedule_time: data.schedule_time || '',
        duration_minutes: data.duration_minutes || 60,
        max_participants: data.max_participants || 100,
        is_active: data.is_active !== false
      });
    } else {
      setFormData({
        title: '',
        description: '',
        program: PROGRAMS[0] || '',
        mapel: '',
        instructor: '',
        zoom_link: '',
        schedule_date: '',
        schedule_time: '',
        duration_minutes: 60,
        max_participants: 100,
        is_active: true
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Kelas Zoom' : 'Edit Kelas Zoom'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Judul Kelas</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Kelas Intensif TWK"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi kelas"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Program</Label>
              <Select value={formData.program} onValueChange={(v) => setFormData({ ...formData, program: v, mapel: '' })}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih Program" />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map(prog => (
                    <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mata Pelajaran</Label>
              <Select value={formData.mapel} onValueChange={(v) => setFormData({ ...formData, mapel: v })} disabled={!formData.program}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih Mapel" />
                </SelectTrigger>
                <SelectContent>
                  {mapelOptions.map(mapel => (
                    <SelectItem key={mapel} value={mapel}>{mapel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Instruktur</Label>
            <Input
              value={formData.instructor}
              onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
              placeholder="Nama instruktur"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Link Zoom</Label>
            <Input
              value={formData.zoom_link}
              onChange={(e) => setFormData({ ...formData, zoom_link: e.target.value })}
              placeholder="https://zoom.us/j/..."
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tanggal</Label>
              <Input
                type="date"
                value={formData.schedule_date}
                onChange={(e) => setFormData({ ...formData, schedule_date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Waktu</Label>
              <Input
                type="time"
                value={formData.schedule_time}
                onChange={(e) => setFormData({ ...formData, schedule_time: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Durasi (menit)</Label>
              <Input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                min={15}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Peserta</Label>
              <Input
                type="number"
                value={formData.max_participants}
                onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 100 })}
                min={1}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
            />
            <span>Aktif</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" className="bg-[#4472C4] hover:bg-[#3861a8]">
              {mode === 'create' ? 'Tambah Kelas' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Landing Content Dialog Component
function LandingContentDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    section: 'hero',
    content_type: 'text',
    key: '',
    value: '',
    order: 0,
    is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const sections = ['hero', 'stats', 'commitment', 'tryout', 'video', 'program', 'ebook', 'cta', 'footer'];
  const contentTypes = ['text', 'image', 'video', 'link', 'number'];

  useEffect(() => {
    if (data) {
      setFormData({
        section: data.section || 'hero',
        content_type: data.content_type || 'text',
        key: data.key || '',
        value: data.value || '',
        order: data.order || 0,
        is_active: data.is_active !== false
      });
    } else {
      setFormData({
        section: 'hero',
        content_type: 'text',
        key: '',
        value: '',
        order: 0,
        is_active: true
      });
    }
  }, [data, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      setFormData(prev => ({ ...prev, value: result.url }));
      toast.success('Gambar berhasil diupload');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Gagal mengupload gambar');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Konten' : 'Edit Konten'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Section</Label>
              <Select value={formData.section} onValueChange={(v) => setFormData({ ...formData, section: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sections.map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipe Konten</Label>
              <Select value={formData.content_type} onValueChange={(v) => setFormData({ ...formData, content_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contentTypes.map(t => (
                    <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Key (Identifier)</Label>
            <Input
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              placeholder="hero_title, stats_students, etc."
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Value</Label>
            {formData.content_type === 'image' ? (
              <div className="space-y-3">
                {/* Image Preview */}
                {formData.value && (
                  <div className="relative w-full h-32 bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                    <img 
                      src={formData.value} 
                      alt="Preview" 
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                
                {/* Upload Button */}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="landing-image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Mengupload...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {formData.value ? 'Ganti Gambar' : 'Upload Gambar'}
                      </>
                    )}
                  </Button>
                </div>
                
                {/* URL Input as alternative */}
                <div className="relative">
                  <Input
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder="Atau masukkan URL gambar"
                    className="pr-10"
                  />
                  {formData.value && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, value: '' })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500">Format: JPG, PNG, GIF, WebP. Maksimal 5MB</p>
              </div>
            ) : (
              <Textarea
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="Konten atau URL"
                required
                rows={4}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Urutan</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                min={0}
              />
            </div>
            <div className="space-y-2 flex items-end">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <span>Aktif</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" className="bg-[#4472C4] hover:bg-[#3861a8]">
              {mode === 'create' ? 'Tambah Konten' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Subject (Mata Pelajaran) Dialog Component
function SubjectDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    program: '',
    name: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        program: data.program || '',
        name: data.name || '',
        description: data.description || '',
        is_active: data.is_active !== false
      });
    } else {
      setFormData({
        program: '',
        name: '',
        description: '',
        is_active: true
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Mata Pelajaran' : 'Edit Mata Pelajaran'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Tambahkan mata pelajaran baru untuk program tertentu' : 'Ubah data mata pelajaran'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Program *</Label>
            <Select 
              value={formData.program} 
              onValueChange={(v) => setFormData({ ...formData, program: v })}
            >
              <SelectTrigger data-testid="subject-program-select">
                <SelectValue placeholder="Pilih Program" />
              </SelectTrigger>
              <SelectContent>
                {PROGRAMS.map(prog => (
                  <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Nama Mata Pelajaran *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Contoh: TWK, TIU, TKP"
              required
              data-testid="subject-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi singkat mata pelajaran"
              data-testid="subject-description-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              data-testid="subject-active-switch"
            />
            <span className="text-sm">Aktif</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button 
              type="submit" 
              className="bg-[#4472C4] hover:bg-[#3861a8]"
              disabled={!formData.program || !formData.name}
              data-testid="subject-save-btn"
            >
              {mode === 'create' ? 'Tambah' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Category (Kategori) Dialog Component
function CategoryDialog({ open, mode, data, subjects, onClose, onSave }) {
  const [formData, setFormData] = useState({
    program: '',
    subject: '',
    name: '',
    description: '',
    is_active: true
  });

  // Get available subjects for selected program
  const availableSubjects = subjects?.filter(s => s.program === formData.program) || [];

  useEffect(() => {
    if (data) {
      setFormData({
        program: data.program || '',
        subject: data.subject || '',
        name: data.name || '',
        description: data.description || '',
        is_active: data.is_active !== false
      });
    } else {
      setFormData({
        program: '',
        subject: '',
        name: '',
        description: '',
        is_active: true
      });
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Tambah Kategori' : 'Edit Kategori'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Tambahkan kategori baru untuk mata pelajaran tertentu' : 'Ubah data kategori'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Program *</Label>
            <Select 
              value={formData.program} 
              onValueChange={(v) => setFormData({ ...formData, program: v, subject: '' })}
            >
              <SelectTrigger data-testid="category-program-select">
                <SelectValue placeholder="Pilih Program" />
              </SelectTrigger>
              <SelectContent>
                {PROGRAMS.map(prog => (
                  <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Mata Pelajaran *</Label>
            <Select 
              value={formData.subject} 
              onValueChange={(v) => setFormData({ ...formData, subject: v })}
              disabled={!formData.program}
            >
              <SelectTrigger data-testid="category-subject-select">
                <SelectValue placeholder={formData.program ? "Pilih Mata Pelajaran" : "Pilih Program dulu"} />
              </SelectTrigger>
              <SelectContent>
                {availableSubjects.length > 0 ? (
                  availableSubjects.map(subj => (
                    <SelectItem key={subj.id} value={subj.name}>{subj.name}</SelectItem>
                  ))
                ) : (
                  <SelectItem value="__none__" disabled>Tidak ada mata pelajaran</SelectItem>
                )}
              </SelectContent>
            </Select>
            {formData.program && availableSubjects.length === 0 && (
              <p className="text-xs text-amber-600">
                Belum ada mata pelajaran untuk program ini. Tambahkan di menu Mata Pelajaran terlebih dahulu.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Nama Kategori *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Contoh: TWK-1, TWK-2"
              required
              data-testid="category-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi singkat kategori"
              data-testid="category-description-input"
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              data-testid="category-active-switch"
            />
            <span className="text-sm">Aktif</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button 
              type="submit" 
              className="bg-[#4472C4] hover:bg-[#3861a8]"
              disabled={!formData.program || !formData.subject || !formData.name}
              data-testid="category-save-btn"
            >
              {mode === 'create' ? 'Tambah' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Enhanced Landing Content Tab Component
function LandingContentTab({ landingContent, onAdd, onEdit, onDelete, onRefresh }) {
  const [activeSection, setActiveSection] = useState('all');
  
  // Define content type configurations
  const CONTENT_TYPES = {
    hero: { label: 'Hero Banner', color: 'bg-blue-100 text-blue-700', icon: '🏠' },
    statistik: { label: 'Statistik', color: 'bg-pink-100 text-pink-700', icon: '📊' },
    fitur: { label: 'Fitur/Keunggulan', color: 'bg-green-100 text-green-700', icon: '✨' },
    program: { label: 'Program', color: 'bg-purple-100 text-purple-700', icon: '📚' },
    tryout: { label: 'Try Out', color: 'bg-indigo-100 text-indigo-700', icon: '📝' },
    video: { label: 'Video', color: 'bg-red-100 text-red-700', icon: '🎬' },
    ebook: { label: 'E-Book', color: 'bg-orange-100 text-orange-700', icon: '📖' },
    testimoni: { label: 'Testimoni', color: 'bg-yellow-100 text-yellow-700', icon: '💬' },
    promo: { label: 'Promo Banner', color: 'bg-rose-100 text-rose-700', icon: '🎁' },
    pengumuman: { label: 'Pengumuman', color: 'bg-cyan-100 text-cyan-700', icon: '📢' },
    footer: { label: 'Footer', color: 'bg-slate-100 text-slate-700', icon: '📄' },
    cta: { label: 'CTA', color: 'bg-emerald-100 text-emerald-700', icon: '🎯' },
    commitment: { label: 'Commitment', color: 'bg-amber-100 text-amber-700', icon: '🤝' },
    stats: { label: 'Stats', color: 'bg-teal-100 text-teal-700', icon: '📈' }
  };

  // Count items per section
  const sectionCounts = landingContent.reduce((acc, item) => {
    const section = item.section?.toLowerCase() || 'other';
    acc[section] = (acc[section] || 0) + 1;
    return acc;
  }, {});

  // Get unique sections from content
  const sections = [...new Set(landingContent.map(c => c.section?.toLowerCase() || 'other'))];

  // Filter content by active section
  const filteredContent = activeSection === 'all' 
    ? landingContent 
    : landingContent.filter(c => (c.section?.toLowerCase() || 'other') === activeSection);

  // Sort by order
  const sortedContent = [...filteredContent].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Check if value is an image URL
  const isImageUrl = (value) => {
    if (!value) return false;
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(value) || 
           value.includes('unsplash') || 
           value.includes('cloudinary') ||
           value.includes('imgur') ||
           value.startsWith('data:image');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kelola Konten Landing Page</h1>
          <p className="text-slate-500 mt-1">Atur hero banner, promo, program cards, dan konten lainnya</p>
        </div>
        <Button
          onClick={onAdd}
          className="bg-[#4472C4] hover:bg-[#3861a8]"
          data-testid="add-landing-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Tambah Konten
        </Button>
      </div>

      {/* Section Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        <Badge 
          variant={activeSection === 'all' ? 'default' : 'outline'}
          className={`cursor-pointer transition-colors ${activeSection === 'all' ? 'bg-[#4472C4]' : 'hover:bg-slate-100'}`}
          onClick={() => setActiveSection('all')}
        >
          Semua ({landingContent.length})
        </Badge>
        {sections.map(section => {
          const config = CONTENT_TYPES[section] || { label: section, color: 'bg-slate-100 text-slate-700', icon: '📄' };
          return (
            <Badge 
              key={section}
              variant="outline"
              className={`cursor-pointer transition-colors ${
                activeSection === section 
                  ? config.color + ' border-transparent' 
                  : 'hover:bg-slate-100'
              }`}
              onClick={() => setActiveSection(section)}
            >
              {config.label} ({sectionCounts[section] || 0})
            </Badge>
          );
        })}
      </div>

      {/* Content Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-600 w-8"></th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Judul</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Tipe</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Preview</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600 w-20">Urutan</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600 w-24">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {sortedContent.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-12 text-center">
                    <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-semibold text-slate-900 mb-1">Belum Ada Konten</h3>
                    <p className="text-sm text-slate-500">
                      {activeSection === 'all' 
                        ? 'Tambahkan konten landing page' 
                        : `Belum ada konten untuk ${CONTENT_TYPES[activeSection]?.label || activeSection}`
                      }
                    </p>
                  </td>
                </tr>
              ) : (
                sortedContent.map((c, i) => {
                  const section = c.section?.toLowerCase() || 'other';
                  const config = CONTENT_TYPES[section] || { label: section, color: 'bg-slate-100 text-slate-700', icon: '📄' };
                  
                  return (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`landing-row-${i}`}>
                      {/* Drag handle placeholder */}
                      <td className="p-4 text-slate-400">
                        <div className="flex flex-col gap-0.5">
                          <div className="w-4 h-0.5 bg-slate-300 rounded"></div>
                          <div className="w-4 h-0.5 bg-slate-300 rounded"></div>
                          <div className="w-4 h-0.5 bg-slate-300 rounded"></div>
                        </div>
                      </td>
                      {/* Title with description */}
                      <td className="p-4">
                        <div className="font-medium text-slate-900">{c.key}</div>
                        <div className="text-sm text-slate-500 max-w-xs truncate">
                          {isImageUrl(c.value) ? 'Gambar' : c.value?.substring(0, 50)}
                          {c.value?.length > 50 && !isImageUrl(c.value) ? '...' : ''}
                        </div>
                      </td>
                      {/* Type badge */}
                      <td className="p-4">
                        <Badge className={`${config.color} border-0`}>
                          <span className="mr-1">{config.icon}</span>
                          {config.label}
                        </Badge>
                      </td>
                      {/* Preview */}
                      <td className="p-4">
                        {isImageUrl(c.value) ? (
                          <img 
                            src={c.value} 
                            alt={c.key}
                            className="w-16 h-12 object-cover rounded border border-slate-200"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ) : c.content_type === 'image' ? (
                          <div className="w-16 h-12 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-slate-400 text-xs">
                            No Image
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      {/* Status */}
                      <td className="p-4">
                        <Badge className={c.is_active 
                          ? 'bg-green-100 text-green-700 border-0' 
                          : 'bg-slate-100 text-slate-500 border-0'
                        }>
                          {c.is_active ? (
                            <><Check className="w-3 h-3 mr-1" /> Aktif</>
                          ) : (
                            'Nonaktif'
                          )}
                        </Badge>
                      </td>
                      {/* Order */}
                      <td className="p-4">
                        <span className="text-slate-600 font-medium">{c.order || 0}</span>
                      </td>
                      {/* Actions */}
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(c)}
                            className="text-slate-600 hover:text-slate-900"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => onDelete(c.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Auto Generate Try Out Dialog Component
function AutoGenerateDialog({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    program: '',
    mapel: '',
    kategori: '',
    question_count: 50,
    duration_minutes: 60,
    price: 0,
    is_active: true,
    randomize: true
  });
  const [availableCount, setAvailableCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  // Get mapel options based on selected program
  const mapelOptions = formData.program ? MAPEL_OPTIONS[formData.program] || [] : [];

  // Check available questions when filters change
  useEffect(() => {
    const checkCount = async () => {
      if (!formData.program) {
        setAvailableCount(0);
        return;
      }
      
      setChecking(true);
      try {
        const params = { program: formData.program };
        if (formData.mapel) params.mapel = formData.mapel;
        if (formData.kategori) params.kategori = formData.kategori;
        
        const res = await tryoutAdminAPI.getQuestionCount(params);
        setAvailableCount(res.data.count);
      } catch (err) {
        console.error('Failed to check count:', err);
        setAvailableCount(0);
      }
      setChecking(false);
    };
    
    if (open) {
      checkCount();
    }
  }, [formData.program, formData.mapel, formData.kategori, open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        title: '',
        description: '',
        program: '',
        mapel: '',
        kategori: '',
        question_count: 50,
        duration_minutes: 60,
        price: 0,
        is_active: true,
        randomize: true
      });
      setAvailableCount(0);
    }
  }, [open]);

  // Generate kategori options
  const kategoriOptions = formData.mapel ? generateKategori(formData.mapel) : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.program || !formData.title) return;
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        mapel: formData.mapel || null,
        kategori: formData.kategori || null
      };
      await tryoutAdminAPI.autoGenerate(submitData);
      toast.success(`Berhasil membuat Try Out dengan ${Math.min(formData.question_count, availableCount)} soal!`);
      onSuccess();
    } catch (err) {
      console.error('Failed to auto-generate:', err);
      toast.error(err.response?.data?.detail || 'Gagal membuat Try Out otomatis');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#4472C4]" />
            Auto-Generate Try Out
          </DialogTitle>
          <DialogDescription>
            Buat Try Out otomatis dengan memilih soal random dari Bank Soal
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label>Judul Try Out *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Contoh: Try Out TWK CPNS 2026 Batch 1"
              required
              data-testid="auto-gen-title"
            />
          </div>

          {/* Program Selection */}
          <div className="space-y-2">
            <Label>Program *</Label>
            <Select 
              value={formData.program} 
              onValueChange={(v) => setFormData({ ...formData, program: v, mapel: '', kategori: '' })}
            >
              <SelectTrigger data-testid="auto-gen-program">
                <SelectValue placeholder="Pilih Program" />
              </SelectTrigger>
              <SelectContent>
                {PROGRAMS.map(prog => (
                  <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mapel Selection (Optional) */}
          <div className="space-y-2">
            <Label>Mata Pelajaran (Opsional)</Label>
            <Select 
              value={formData.mapel || "all"} 
              onValueChange={(v) => setFormData({ ...formData, mapel: v === "all" ? "" : v, kategori: '' })}
              disabled={!formData.program}
            >
              <SelectTrigger data-testid="auto-gen-mapel">
                <SelectValue placeholder="Semua Mapel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Mapel</SelectItem>
                {mapelOptions.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Kategori Selection (Optional) */}
          {formData.mapel && kategoriOptions.length > 0 && (
            <div className="space-y-2">
              <Label>Kategori (Opsional)</Label>
              <Select 
                value={formData.kategori || "all"} 
                onValueChange={(v) => setFormData({ ...formData, kategori: v === "all" ? "" : v })}
              >
                <SelectTrigger data-testid="auto-gen-kategori">
                  <SelectValue placeholder="Semua Kategori" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kategori</SelectItem>
                  {kategoriOptions.map(k => (
                    <SelectItem key={k} value={k}>{k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Available Questions Info */}
          {formData.program && (
            <div className={`p-3 rounded-lg ${availableCount > 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Soal Tersedia:</span>
                <span className={`text-lg font-bold ${availableCount > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                  {checking ? '...' : availableCount} soal
                </span>
              </div>
              {availableCount === 0 && !checking && (
                <p className="text-xs text-amber-600 mt-1">
                  Tidak ada soal yang cocok. Tambahkan soal di Bank Soal terlebih dahulu.
                </p>
              )}
            </div>
          )}

          {/* Question Count */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Jumlah Soal</Label>
              <Input
                type="number"
                value={formData.question_count}
                onChange={(e) => setFormData({ ...formData, question_count: parseInt(e.target.value) || 10 })}
                min={1}
                max={availableCount || 1000}
                data-testid="auto-gen-count"
              />
              {availableCount > 0 && formData.question_count > availableCount && (
                <p className="text-xs text-amber-600">Max: {availableCount} soal</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Durasi (menit)</Label>
              <Input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                min={1}
                data-testid="auto-gen-duration"
              />
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.randomize}
                onCheckedChange={(v) => setFormData({ ...formData, randomize: v })}
                id="randomize"
              />
              <Label htmlFor="randomize" className="text-sm cursor-pointer">Acak Soal</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <span className="text-sm">Aktifkan</span>
            </div>
          </div>

          {/* Price (Optional) */}
          <div className="space-y-2">
            <Label>Harga (Rp) - Kosongkan untuk gratis</Label>
            <Input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
              min={0}
              placeholder="0 = Gratis"
              data-testid="auto-gen-price"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button 
              type="submit" 
              className="bg-[#4472C4] hover:bg-[#3861a8]"
              disabled={!formData.program || !formData.title || availableCount === 0 || loading}
              data-testid="auto-gen-submit"
            >
              {loading ? 'Membuat...' : `Generate ${Math.min(formData.question_count, availableCount || 0)} Soal`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Enhanced Bank Soal Tab Component with Server-Side Pagination
function BankSoalTab({ 
  questions, 
  stats, 
  questionFilter, 
  setQuestionFilter, 
  questionSearch, 
  setQuestionSearch,
  filteredQuestions,
  subjects = [],
  onAddQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onBulkDelete,
  onDownloadTemplate,
  onImportCSV,
  onExportQuestions,
  fileInputRef,
  handleImportCSV,
  setQuestionDialog,
  pagination = { page: 1, total: 0, totalPages: 1, limit: 100 },
  onPageChange,
  loadingQuestions = false,
  questionStats = { total: 0, by_program: {} }
}) {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'hierarchy'
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [quickGenerateOpen, setQuickGenerateOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Fetch categories when filter changes
  useEffect(() => {
    const fetchCategories = async () => {
      if (questionFilter.program && questionFilter.mapel) {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/public/categories?program=${encodeURIComponent(questionFilter.program)}&subject=${encodeURIComponent(questionFilter.mapel)}`);
          setCategories(res.data.filter(c => c.is_active));
        } catch (err) {
          console.error('Failed to fetch categories:', err);
          setCategories([]);
        }
      } else {
        setCategories([]);
      }
    };
    fetchCategories();
  }, [questionFilter.program, questionFilter.mapel]);
  
  // Use server-side pagination data with safe defaults
  const { 
    page: currentPage = 1, 
    total = 0, 
    totalPages = 1, 
    limit: itemsPerPage = 100 
  } = pagination || {};
  
  // Calculate stats per program from server-side stats with safe default
  const programStats = questionStats?.by_program || {};
  
  // Calculate hierarchical data for hierarchy view
  const hierarchyData = useMemo(() => {
    const data = {};
    questions.forEach(q => {
      const prog = q.program || 'Lainnya';
      const mapel = q.mapel || 'Umum';
      const kat = q.kategori || 'Uncategorized';
      
      if (!data[prog]) data[prog] = { count: 0, mapel: {} };
      data[prog].count++;
      
      if (!data[prog].mapel[mapel]) data[prog].mapel[mapel] = { count: 0, kategori: {} };
      data[prog].mapel[mapel].count++;
      
      if (!data[prog].mapel[mapel].kategori[kat]) data[prog].mapel[mapel].kategori[kat] = 0;
      data[prog].mapel[mapel].kategori[kat]++;
    });
    return data;
  }, [questions]);

  // Get program colors
  const getProgramColor = (program) => {
    const colors = {
      'CPNS': 'bg-blue-500',
      'UTBK-SNBT': 'bg-purple-500',
      'TNI': 'bg-green-600',
      'POLRI': 'bg-slate-700',
      'SEKOLAH KEDINASAN': 'bg-orange-500',
      'BUMN': 'bg-red-500',
      'TKA': 'bg-cyan-500',
      'SMA Favorit': 'bg-pink-500'
    };
    return colors[program] || 'bg-slate-500';
  };
  
  // Handle select all on current page
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedQuestions(filteredQuestions.map(q => q.id));
    } else {
      setSelectedQuestions([]);
    }
  };
  
  // Handle individual selection
  const handleSelectQuestion = (questionId, checked) => {
    if (checked) {
      setSelectedQuestions(prev => [...prev, questionId]);
    } else {
      setSelectedQuestions(prev => prev.filter(id => id !== questionId));
    }
  };
  
  // Check if all on current page are selected
  const isAllSelected = filteredQuestions.length > 0 && filteredQuestions.every(q => selectedQuestions.includes(q.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bank Soal</h1>
          <p className="text-slate-500 mt-1">
            Kelola soal untuk semua program 
            <Badge variant="outline" className="ml-2">{(questionStats?.total || total || 0).toLocaleString()} total soal</Badge>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'table' ? 'bg-[#4472C4] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('hierarchy')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'hierarchy' ? 'bg-[#4472C4] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <Layers className="w-4 h-4" />
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={onDownloadTemplate} data-testid="template-btn">
            <File className="w-4 h-4 mr-2" />
            Template
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            className="hidden"
          />
          <Button variant="outline" size="sm" onClick={onImportCSV} data-testid="import-btn">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" size="sm" onClick={onExportQuestions} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            onClick={onAddQuestion}
            className="bg-[#4472C4] hover:bg-[#3861a8]"
            data-testid="add-question-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Tambah Soal
          </Button>
        </div>
      </div>

      {/* Quick Generate Bar - Show when questions are selected */}
      {selectedQuestions.length > 0 && (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox 
                checked={isAllSelected}
                onCheckedChange={handleSelectAll}
              />
              <span className="font-medium text-green-800">
                {selectedQuestions.length} soal dipilih
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedQuestions([])}
              >
                Batal Pilih
              </Button>
              <Button 
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteOpen(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus {selectedQuestions.length} Soal
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                size="sm"
                onClick={() => setQuickGenerateOpen(true)}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Quick Generate Try Out
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Program Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {PROGRAMS.map(program => (
          <Card 
            key={program}
            className={`cursor-pointer transition-all hover:shadow-md ${
              questionFilter.program === program ? 'ring-2 ring-[#4472C4]' : ''
            }`}
            onClick={() => setQuestionFilter(prev => ({ 
              ...prev, 
              program: prev.program === program ? '' : program,
              mapel: '',
              kategori: ''
            }))}
          >
            <CardContent className="p-3 text-center">
              <div className={`w-8 h-8 rounded-lg ${getProgramColor(program)} flex items-center justify-center text-white text-xs font-bold mx-auto mb-2`}>
                {program.substring(0, 2).toUpperCase()}
              </div>
              <div className="text-2xl font-bold text-slate-900">{(programStats[program] || 0).toLocaleString()}</div>
              <div className="text-xs text-slate-500 truncate" title={program}>{program}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Hierarchy View */}
      {viewMode === 'hierarchy' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Struktur Bank Soal
            </CardTitle>
            <CardDescription>Klik untuk filter berdasarkan Program → Mata Pelajaran → Kategori</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(hierarchyData).map(([prog, progData]) => (
                <div key={prog} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuestionFilter(prev => ({ 
                      ...prev, 
                      program: prev.program === prog ? '' : prog,
                      mapel: '',
                      kategori: ''
                    }))}
                    className={`w-full flex items-center justify-between p-3 hover:bg-slate-50 ${
                      questionFilter.program === prog ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded ${getProgramColor(prog)} flex items-center justify-center text-white text-xs font-bold`}>
                        {prog.substring(0, 2).toUpperCase()}
                      </div>
                      <span className="font-semibold">{prog}</span>
                    </div>
                    <Badge variant="secondary">{(progData?.count || 0).toLocaleString()} soal</Badge>
                  </button>
                  
                  {questionFilter.program === prog && (
                    <div className="border-t bg-slate-50/50">
                      {Object.entries(progData?.mapel || {}).map(([mapel, mapelData]) => (
                        <div key={mapel} className="border-b border-slate-100 last:border-0">
                          <button
                            onClick={() => setQuestionFilter(prev => ({ 
                              ...prev, 
                              mapel: prev.mapel === mapel ? '' : mapel,
                              kategori: ''
                            }))}
                            className={`w-full flex items-center justify-between p-3 pl-12 hover:bg-slate-100 ${
                              questionFilter.mapel === mapel ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <BookOpen className="w-4 h-4 text-slate-400" />
                              <span>{mapel}</span>
                            </div>
                            <Badge variant="outline">{(mapelData?.count || 0).toLocaleString()} soal</Badge>
                          </button>
                          
                          {questionFilter.mapel === mapel && (
                            <div className="bg-white">
                              {Object.entries(mapelData.kategori).map(([kat, katCount]) => (
                                <button
                                  key={kat}
                                  onClick={() => setQuestionFilter(prev => ({ 
                                    ...prev, 
                                    kategori: prev.kategori === kat ? '' : kat
                                  }))}
                                  className={`w-full flex items-center justify-between p-2 pl-20 hover:bg-slate-50 text-sm ${
                                    questionFilter.kategori === kat ? 'bg-green-50 text-green-700' : ''
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Tag className="w-3 h-3 text-slate-400" />
                                    <span>{kat}</span>
                                  </div>
                                  <span className="text-slate-500">{katCount} soal</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Search className="w-4 h-4" />
              Filter:
            </div>
            {/* Access Type Filter - Gratis vs Berbayar */}
            <Select
              value={questionFilter.access_type || 'all'}
              onValueChange={(v) => setQuestionFilter(prev => ({ ...prev, access_type: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-36" data-testid="filter-access-type">
                <SelectValue placeholder="Semua Tipe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Tipe</SelectItem>
                <SelectItem value="gratis">🟢 Gratis</SelectItem>
                <SelectItem value="berbayar">🟡 Berbayar</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={questionFilter.program || 'all'}
              onValueChange={(v) => setQuestionFilter(prev => ({ ...prev, program: v === 'all' ? '' : v, mapel: '', kategori: '' }))}
            >
              <SelectTrigger className="w-48" data-testid="filter-program">
                <SelectValue placeholder="Semua Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Program</SelectItem>
                {PROGRAMS.map(prog => (
                  <SelectItem key={prog} value={prog}>
                    {prog} ({(programStats[prog] || 0).toLocaleString()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={questionFilter.mapel || 'all'}
              onValueChange={(v) => setQuestionFilter(prev => ({ ...prev, mapel: v === 'all' ? '' : v, kategori: '' }))}
              disabled={!questionFilter.program}
            >
              <SelectTrigger className="w-48" data-testid="filter-mapel">
                <SelectValue placeholder={questionFilter.program ? "Pilih Mapel" : "Pilih Program dulu"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Mapel</SelectItem>
                {/* Use subjects from database, fallback to hardcoded if empty */}
                {(questionFilter.program 
                  ? (subjects.filter(s => s.program === questionFilter.program && s.is_active !== false).length > 0
                      ? subjects.filter(s => s.program === questionFilter.program && s.is_active !== false).map(s => s.name)
                      : MAPEL_OPTIONS[questionFilter.program] || [])
                  : []
                )?.map(mapel => (
                  <SelectItem key={mapel} value={mapel}>{mapel}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={questionFilter.kategori || 'all'}
              onValueChange={(v) => setQuestionFilter(prev => ({ ...prev, kategori: v === 'all' ? '' : v }))}
              disabled={!questionFilter.mapel}
            >
              <SelectTrigger className="w-48" data-testid="filter-kategori">
                <SelectValue placeholder={questionFilter.mapel ? "Pilih Kategori" : "Pilih Mapel dulu"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kategori</SelectItem>
                {/* Filter categories by program and subject */}
                {categories
                  .filter(cat => 
                    (!questionFilter.program || cat.program === questionFilter.program) &&
                    (!questionFilter.mapel || cat.subject === questionFilter.mapel)
                  )
                  .map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cari soal..."
                  value={questionSearch}
                  onChange={(e) => setQuestionSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-question-input"
                />
              </div>
            </div>
            <Badge variant="secondary" className="py-1.5">
              {(filteredQuestions?.length || 0).toLocaleString()} soal ditemukan
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Questions Table with Pagination */}
      {viewMode === 'table' && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-4 w-10">
                    <Checkbox 
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 w-12">#</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Soal</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 w-32">Program</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 w-28">Mapel</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 w-28">Kategori</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 w-24">Tipe</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 w-20">Waktu</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600 w-24">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loadingQuestions ? (
                  <tr>
                    <td colSpan={9} className="p-12 text-center">
                      <Loader2 className="w-8 h-8 text-[#4472C4] mx-auto animate-spin mb-3" />
                      <p className="text-slate-500">Memuat soal...</p>
                    </td>
                  </tr>
                ) : filteredQuestions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-12 text-center">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="font-semibold text-slate-900 mb-1">
                        {questionFilter.program ? `Belum ada soal untuk ${questionFilter.program}` : 'Belum Ada Soal'}
                      </h3>
                      <p className="text-sm text-slate-500 mb-4">
                        {questionFilter.program 
                          ? 'Tambahkan soal baru atau import dari CSV' 
                          : 'Tambahkan soal baru untuk memulai'}
                      </p>
                      <Button onClick={onAddQuestion} size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Soal
                      </Button>
                    </td>
                  </tr>
                ) : (
                  filteredQuestions.map((q, i) => (
                    <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`question-row-${i}`}>
                      <td className="p-4">
                        <Checkbox 
                          checked={selectedQuestions.includes(q.id)}
                          onCheckedChange={(checked) => handleSelectQuestion(q.id, checked)}
                        />
                      </td>
                      <td className="p-4 text-slate-500 text-sm font-medium">{((currentPage - 1) * itemsPerPage) + i + 1}</td>
                      <td className="p-4">
                        <div className="text-slate-900 line-clamp-2 max-w-md">
                          {stripHtmlForPreview(q.text)}
                          {q.text?.length > 150 ? '...' : ''}
                        </div>
                        {q.options && q.options.length > 0 && (
                          <p className="text-xs text-slate-400 mt-1">{q.options.length} opsi jawaban</p>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge className={`${getProgramColor(q.program)} text-white border-0 text-xs`}>
                          {q.program || q.category}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-600">{q.mapel || '-'}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-500">{q.kategori || '-'}</span>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="text-xs">
                          {q.question_type === 'multiple_choice_weighted' ? 'Berbobot' : 'PG'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 text-slate-500 text-sm">
                          <Clock className="w-3 h-3" />
                          {q.time_limit || 60}s
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewQuestion(q)}
                            title="Preview Soal"
                            data-testid={`preview-question-${i}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditQuestion(q)}
                            data-testid={`edit-question-${i}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => onDeleteQuestion(q.id)}
                            data-testid={`delete-question-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls - Server Side */}
          {(total || 0) > 0 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>Total:</span>
                <Badge variant="secondary" className="font-bold">
                  {(total || 0).toLocaleString()} soal
                </Badge>
                {loadingQuestions && (
                  <Loader2 className="w-4 h-4 animate-spin text-[#4472C4]" />
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">
                  Halaman {currentPage || 1} dari {totalPages || 1}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange && onPageChange(1)}
                    disabled={currentPage === 1 || loadingQuestions}
                    data-testid="pagination-first"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange && onPageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loadingQuestions}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  {/* Quick page jumps */}
                  {[...Array(Math.min(5, totalPages || 1))].map((_, i) => {
                    let pageNum;
                    const safeTotalPages = totalPages || 1;
                    const safeCurrentPage = currentPage || 1;
                    
                    if (safeTotalPages <= 5) {
                      pageNum = i + 1;
                    } else if (safeCurrentPage <= 3) {
                      pageNum = i + 1;
                    } else if (safeCurrentPage >= safeTotalPages - 2) {
                      pageNum = safeTotalPages - 4 + i;
                    } else {
                      pageNum = safeCurrentPage - 2 + i;
                    }
                    
                    if (pageNum < 1 || pageNum > safeTotalPages) return null;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => onPageChange && onPageChange(pageNum)}
                        disabled={loadingQuestions}
                        className={pageNum === currentPage ? "bg-[#4472C4]" : ""}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange && onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || loadingQuestions}
                    data-testid="pagination-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange && onPageChange(totalPages)}
                    disabled={currentPage >= totalPages || loadingQuestions}
                    data-testid="pagination-last"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Preview Question Dialog */}
      <Dialog open={!!previewQuestion} onOpenChange={(o) => !o && setPreviewQuestion(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview Soal
            </DialogTitle>
          </DialogHeader>
          {previewQuestion && (
            <div className="space-y-4">
              {/* Question Metadata */}
              <div className="flex flex-wrap gap-2">
                <Badge className={`${getProgramColor(previewQuestion.program)} text-white border-0`}>
                  {previewQuestion.program || previewQuestion.category}
                </Badge>
                {previewQuestion.mapel && (
                  <Badge variant="outline">{previewQuestion.mapel}</Badge>
                )}
                {previewQuestion.kategori && (
                  <Badge variant="secondary">{previewQuestion.kategori}</Badge>
                )}
                <Badge variant="outline" className="ml-auto">
                  {previewQuestion.question_type || 'multiple_choice'}
                </Badge>
              </div>

              {/* Question Text */}
              <div className="border border-slate-200 rounded-lg p-4 bg-white">
                <Label className="text-xs text-slate-500 mb-2 block">Teks Soal:</Label>
                <div 
                  className="prose prose-sm max-w-none admin-question-preview"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(previewQuestion.text) }}
                />
              </div>

              {/* Options */}
              {previewQuestion.options && previewQuestion.options.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm">
                    Pilihan Jawaban:
                    {/* Show info for weighted/special types */}
                    {['multiple_choice_weighted', 'likert', 'ab'].includes(previewQuestion.question_type) && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">
                        (Skor berdasarkan bobot, tidak ada jawaban benar tunggal)
                      </span>
                    )}
                  </Label>
                  {previewQuestion.options.map((opt, idx) => {
                    // Check if this question type has a single correct answer
                    // TKP, Likert, A/B - NO correct answer, only weights
                    const qType = previewQuestion.question_type || 'multiple_choice';
                    const hasCorrectAnswer = !['multiple_choice_weighted', 'likert', 'ab'].includes(qType);
                    const showAsCorrect = hasCorrectAnswer && opt.is_correct;
                    
                    // For TKP/weighted: highlight highest weight option
                    const isWeightedType = ['multiple_choice_weighted', 'likert', 'ab'].includes(qType);
                    const maxWeight = isWeightedType ? Math.max(...previewQuestion.options.map(o => o.weight || 0)) : 0;
                    const isHighestWeight = isWeightedType && (opt.weight || 0) === maxWeight && maxWeight > 0;
                    
                    return (
                      <div 
                        key={opt.id || idx}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          showAsCorrect 
                            ? 'bg-green-50 border-green-300'
                            : isHighestWeight
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-white border-slate-200'
                        }`}
                      >
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                          showAsCorrect 
                            ? 'bg-green-600 text-white'
                            : isHighestWeight
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-200 text-slate-600'
                        }`}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <div className="flex-1">
                          <span 
                            className="text-sm"
                            dangerouslySetInnerHTML={{ __html: sanitizeHTML(opt.text?.replace(/<p class="MsoNormal"[^>]*>/gi, '').replace(/<\/p>/gi, '').replace(/<span[^>]*>/gi, '').replace(/<\/span>/gi, '').replace(/<o:p><\/o:p>/gi, '').trim() || opt.text) }}
                          />
                          {/* Show weight for weighted question types */}
                          {isWeightedType && (
                            <span className={`ml-2 text-xs font-medium ${(opt.weight || 0) >= 4 ? 'text-green-600' : (opt.weight || 0) >= 2 ? 'text-blue-600' : 'text-slate-500'}`}>
                              (Bobot: {opt.weight || 0})
                            </span>
                          )}
                        </div>
                        {/* Only show checkmark for question types with single correct answer */}
                        {showAsCorrect && (
                          <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                        )}
                        {/* Show star for highest weight in TKP */}
                        {isHighestWeight && (
                          <Star className="w-5 h-5 text-blue-600 shrink-0 fill-blue-600" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Explanation - Support HTML from RichTextEditor */}
              {previewQuestion.explanation && (
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <Label className="text-xs text-blue-600 mb-2 block">Pembahasan:</Label>
                  <div 
                    className="text-sm text-slate-700 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(previewQuestion.explanation) }}
                  />
                </div>
              )}

              {/* Metadata Footer */}
              <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Waktu: {previewQuestion.time_limit || 60}s
                </span>
                <span>Poin: {previewQuestion.points || 10}</span>
                <span>Kesulitan: {previewQuestion.difficulty || 'medium'}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewQuestion(null)}>Tutup</Button>
            <Button onClick={() => {
              setQuestionDialog({ open: true, mode: 'edit', data: previewQuestion });
              setPreviewQuestion(null);
            }}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit Soal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Generate Dialog */}
      <QuickGenerateDialog 
        open={quickGenerateOpen}
        onClose={() => setQuickGenerateOpen(false)}
        selectedIds={selectedQuestions}
        questions={questions}
        onSuccess={() => {
          setQuickGenerateOpen(false);
          setSelectedQuestions([]);
          // Trigger reload from parent
        }}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {selectedQuestions.length} Soal</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus {selectedQuestions.length} soal yang dipilih? 
              Tindakan ini tidak dapat dibatalkan dan soal akan dihapus dari semua try out yang menggunakannya.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                try {
                  await questionAPI.bulkDelete(selectedQuestions);
                  setBulkDeleteOpen(false);
                  setSelectedQuestions([]);
                  toast.success(`${selectedQuestions.length} soal berhasil dihapus`);
                  // Trigger parent reload
                  if (onBulkDelete) onBulkDelete();
                } catch (err) {
                  console.error('Bulk delete failed:', err);
                  toast.error('Gagal menghapus soal');
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Hapus {selectedQuestions.length} Soal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Quick Generate Try Out Dialog - Buat Try Out dari soal yang dipilih
function QuickGenerateDialog({ open, onClose, selectedIds, questions, onSuccess }) {
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Get selected questions details
  const selectedQuestions = questions.filter(q => selectedIds.includes(q.id));
  
  // Group by program
  const programGroups = selectedQuestions.reduce((acc, q) => {
    const prog = q.program || 'Lainnya';
    if (!acc[prog]) acc[prog] = [];
    acc[prog].push(q);
    return acc;
  }, {});
  
  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      const mainProgram = Object.keys(programGroups)[0] || '';
      setTitle(`Try Out ${mainProgram} - ${selectedIds.length} Soal`);
      setDuration(Math.ceil(selectedIds.length * 1.5)); // ~1.5 min per soal
      setPrice(0);
    }
  }, [open, selectedIds.length]);
  
  const handleGenerate = async () => {
    if (!title || selectedIds.length === 0) return;
    
    setLoading(true);
    try {
      const mainProgram = Object.keys(programGroups)[0] || 'Umum';
      
      // Call API to create try out with selected questions
      const res = await tryoutAdminAPI.create({
        title,
        description: `Try Out dengan ${selectedIds.length} soal yang dipilih dari Bank Soal`,
        category: mainProgram,
        duration_minutes: duration,
        question_ids: selectedIds,
        price,
        is_active: true
      });
      
      toast.success(`Try Out "${title}" berhasil dibuat dengan ${selectedIds.length} soal!`);
      onSuccess();
    } catch (err) {
      console.error('Failed to create try out:', err);
      toast.error(err.response?.data?.detail || 'Gagal membuat Try Out');
    }
    setLoading(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-green-600" />
            Quick Generate Try Out
          </DialogTitle>
          <DialogDescription>
            Buat Try Out langsung dari {selectedIds.length} soal yang dipilih
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary of selected questions */}
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <h4 className="font-medium mb-3">Ringkasan Soal Terpilih</h4>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(programGroups).map(([prog, qs]) => (
                  <div key={prog} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{prog}</span>
                    <Badge variant="secondary">{qs.length} soal</Badge>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t flex items-center justify-between font-medium">
                <span>Total</span>
                <Badge className="bg-green-600">{selectedIds.length} soal</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Form fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Judul Try Out *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Masukkan judul Try Out"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Durasi (menit)</Label>
                <Input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
                  min={1}
                />
                <p className="text-xs text-slate-500">
                  Rata-rata {(duration / selectedIds.length).toFixed(1)} menit/soal
                </p>
              </div>
              <div className="space-y-2">
                <Label>Harga (Rp)</Label>
                <Input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(parseInt(e.target.value) || 0)}
                  min={0}
                  placeholder="0 = Gratis"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={handleGenerate}
            disabled={loading || !title || selectedIds.length === 0}
          >
            {loading ? 'Membuat...' : `Buat Try Out (${selectedIds.length} soal)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Generate Paket Lengkap Dialog Component
function GeneratePaketDialog({ open, onClose, onSuccess }) {
  const [program, setProgram] = useState('');
  const [batchNumber, setBatchNumber] = useState(1);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [mapelConfig, setMapelConfig] = useState([]);
  const [totalSoal, setTotalSoal] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);

  // All 8 programs with colors
  const ALL_PROGRAMS = [
    { key: 'CPNS', name: 'CPNS', color: 'bg-blue-500', abbr: 'CP' },
    { key: 'UTBK-SNBT', name: 'UTBK-SNBT', color: 'bg-purple-500', abbr: 'UT' },
    { key: 'TNI', name: 'TNI', color: 'bg-green-600', abbr: 'TN' },
    { key: 'POLRI', name: 'POLRI', color: 'bg-red-500', abbr: 'PO' },
    { key: 'SEKOLAH KEDINASAN', name: 'Sekolah Kedinasan', color: 'bg-amber-500', abbr: 'SK' },
    { key: 'BUMN', name: 'BUMN', color: 'bg-cyan-500', abbr: 'BU' },
    { key: 'TKA', name: 'TKA', color: 'bg-indigo-500', abbr: 'TK' },
    { key: 'SMA Favorit', name: 'SMA Favorit', color: 'bg-pink-500', abbr: 'SM' }
  ];

  // Default templates for CPNS and UTBK (sesuai standar resmi)
  const DEFAULT_TEMPLATES = {
    'CPNS': [
      { name: 'TWK', count: 30, time: 25 },
      { name: 'TIU', count: 35, time: 30 },
      { name: 'TKP', count: 45, time: 45 }
    ],
    'UTBK-SNBT': [
      { name: 'Penalaran Umum', count: 20, time: 25 },
      { name: 'Pengetahuan dan Pemahaman Umum', count: 20, time: 25 },
      { name: 'Pemahaman Bacaan dan Menulis', count: 20, time: 25 },
      { name: 'Pengetahuan Kuantitatif', count: 20, time: 25 },
      { name: 'Literasi Bahasa Indonesia', count: 30, time: 40 },
      { name: 'Literasi Bahasa Inggris', count: 20, time: 25 },
      { name: 'Penalaran Matematika', count: 25, time: 30 }
    ]
  };

  // Fetch subjects when program changes
  useEffect(() => {
    if (!program || !open) {
      setSubjects([]);
      setMapelConfig([]);
      return;
    }
    
    const fetchSubjects = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/subjects?program=${encodeURIComponent(program)}`);
        const subjectList = res.data.filter(s => s.is_active);
        setSubjects(subjectList);
        
        // If CPNS or UTBK-SNBT, use default template
        if (DEFAULT_TEMPLATES[program]) {
          setMapelConfig(DEFAULT_TEMPLATES[program].map(m => ({ ...m })));
        } else {
          // For other programs, create config from subjects with default values
          setMapelConfig(subjectList.map(s => ({
            name: s.name,
            count: 10,
            time: 15
          })));
        }
      } catch (err) {
        console.error('Failed to fetch subjects:', err);
      }
    };
    
    fetchSubjects();
  }, [program, open]);

  // Calculate totals when mapelConfig changes
  useEffect(() => {
    const soal = mapelConfig.reduce((sum, m) => sum + (m.count || 0), 0);
    const dur = mapelConfig.reduce((sum, m) => sum + (m.time || 0), 0);
    setTotalSoal(soal);
    setTotalDuration(dur);
  }, [mapelConfig]);

  // Check availability when program changes
  useEffect(() => {
    if (!program || !open) {
      setAvailability(null);
      return;
    }
    
    const checkAvailability = async () => {
      setCheckingAvailability(true);
      try {
        const res = await tryoutAdminAPI.getPaketAvailability(program);
        setAvailability(res.data);
      } catch (err) {
        console.error('Failed to check availability:', err);
        setAvailability(null);
      }
      setCheckingAvailability(false);
    };
    
    checkAvailability();
  }, [program, open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setProgram('');
      setBatchNumber(1);
      setTitle('');
      setPrice(0);
      setAvailability(null);
      setMapelConfig([]);
      setSubjects([]);
    }
  }, [open]);

  // Update title when program or batch changes
  useEffect(() => {
    if (program) {
      setTitle(`Paket ${program} Batch ${batchNumber}`);
    }
  }, [program, batchNumber]);

  // Update mapel config value
  const updateMapelConfig = (index, field, value) => {
    setMapelConfig(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: parseInt(value) || 0 };
      return updated;
    });
  };

  const handleGenerate = async () => {
    if (!program || mapelConfig.length === 0) return;
    
    setLoading(true);
    try {
      const res = await tryoutAdminAPI.generatePaketCustom({
        title,
        program,
        batch_number: batchNumber,
        price,
        is_active: true,
        randomize: true,
        mapel_config: mapelConfig
      });
      
      const data = res.data;
      
      if (data.missing_mapel && data.missing_mapel.length > 0) {
        const missingInfo = data.missing_mapel.map(m => `${m.mapel}: ${m.available}/${m.required}`).join(', ');
        toast.success(`Paket berhasil dibuat dengan ${data.total_questions} soal! (Kurang: ${missingInfo})`);
      } else {
        toast.success(`Paket lengkap berhasil dibuat dengan ${data.total_questions} soal!`);
      }
      
      onSuccess();
    } catch (err) {
      console.error('Failed to generate paket:', err);
      toast.error(err.response?.data?.detail || 'Gagal membuat paket');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-green-600" />
            Generate Paket Lengkap
          </DialogTitle>
          <DialogDescription>
            Buat paket try out lengkap untuk semua program dengan konfigurasi jumlah soal dan waktu
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Program Selection - All 8 Programs */}
          <div className="space-y-2">
            <Label>Pilih Program *</Label>
            <div className="grid grid-cols-4 gap-3">
              {ALL_PROGRAMS.map((p) => (
                <Card 
                  key={p.key}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    program === p.key ? 'ring-2 ring-green-500 bg-green-50' : ''
                  }`}
                  onClick={() => setProgram(p.key)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xs ${p.color}`}>
                        {p.abbr}
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">{p.name}</h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Mapel Configuration - Editable */}
          {program && mapelConfig.length > 0 && (
            <div className="space-y-3">
              <Label className="flex items-center justify-between">
                <span>Konfigurasi Mata Pelajaran</span>
                <span className="text-sm text-slate-500 font-normal">
                  Total: {totalSoal} soal • {totalDuration} menit
                </span>
              </Label>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 pb-2 border-b">
                      <div className="col-span-5">Mata Pelajaran</div>
                      <div className="col-span-3 text-center">Jumlah Soal</div>
                      <div className="col-span-3 text-center">Waktu (menit)</div>
                      <div className="col-span-1 text-center">Bank</div>
                    </div>
                    
                    {/* Rows */}
                    {mapelConfig.map((m, i) => {
                      const avail = availability?.availability?.find(a => 
                        a.mapel.toLowerCase() === m.name.toLowerCase()
                      );
                      const available = avail?.available || 0;
                      
                      return (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-5">
                            <span className="font-medium text-sm">{m.name}</span>
                          </div>
                          <div className="col-span-3">
                            <Input
                              type="number"
                              value={m.count}
                              onChange={(e) => updateMapelConfig(i, 'count', e.target.value)}
                              min={0}
                              className="h-8 text-center"
                            />
                          </div>
                          <div className="col-span-3">
                            <Input
                              type="number"
                              value={m.time}
                              onChange={(e) => updateMapelConfig(i, 'time', e.target.value)}
                              min={0}
                              className="h-8 text-center"
                            />
                          </div>
                          <div className="col-span-1 text-center">
                            <Badge className={
                              available >= m.count ? 'bg-green-100 text-green-700' :
                              available > 0 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {checkingAvailability ? '...' : available}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Total Row */}
                    <div className="grid grid-cols-12 gap-2 items-center pt-2 border-t font-semibold">
                      <div className="col-span-5">Total</div>
                      <div className="col-span-3 text-center">{totalSoal} soal</div>
                      <div className="col-span-3 text-center">{totalDuration} menit</div>
                      <div className="col-span-1"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Availability Warning */}
              {availability && !availability.is_complete && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm">
                    Bank soal belum lengkap. Paket akan dibuat dengan soal yang tersedia.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Batch, Title & Price */}
          {program && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Batch Number</Label>
                  <Input
                    type="number"
                    value={batchNumber}
                    onChange={(e) => setBatchNumber(parseInt(e.target.value) || 1)}
                    min={1}
                    data-testid="paket-batch"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Harga (Rp)</Label>
                  <Input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(parseInt(e.target.value) || 0)}
                    min={0}
                    placeholder="0 = Gratis"
                    data-testid="paket-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Judul Paket</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={`Paket ${program} Batch ${batchNumber}`}
                    data-testid="paket-title"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
          <Button 
            onClick={handleGenerate}
            className="bg-green-600 hover:bg-green-700"
            disabled={!program || loading || mapelConfig.length === 0}
            data-testid="paket-gen-submit"
          >
            {loading ? 'Membuat...' : `Generate Paket`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Subscription Tier Dialog Component
function SubscriptionTierDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: 'basic',
    display_name: '',
    description: '',
    price: 0,
    duration_days: 30,
    max_tryouts_per_month: 5,
    programs: [],
    features: [],
    includes_zoom: false,
    zoom_sessions_per_month: 0,
    includes_ebook: false,
    includes_wag: false,
    wag_type: 'regular',
    color: '#4472C4',
    is_active: true,
    sort_order: 1
  });
  const [newFeature, setNewFeature] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data && mode === 'edit') {
      setFormData({
        name: data.name || 'basic',
        display_name: data.display_name || '',
        description: data.description || '',
        price: data.price || 0,
        duration_days: data.duration_days || 30,
        max_tryouts_per_month: data.max_tryouts_per_month || 5,
        programs: data.programs || [],
        features: data.features || [],
        includes_zoom: data.includes_zoom || false,
        zoom_sessions_per_month: data.zoom_sessions_per_month || 0,
        includes_ebook: data.includes_ebook || false,
        includes_wag: data.includes_wag || false,
        wag_type: data.wag_type || 'regular',
        color: data.color || '#4472C4',
        is_active: data.is_active !== false,
        sort_order: data.sort_order || 1
      });
    } else {
      setFormData({
        name: 'basic',
        display_name: '',
        description: '',
        price: 0,
        duration_days: 30,
        max_tryouts_per_month: 5,
        programs: [],
        features: [],
        includes_zoom: false,
        zoom_sessions_per_month: 0,
        includes_ebook: false,
        includes_wag: false,
        wag_type: 'regular',
        color: '#4472C4',
        is_active: true,
        sort_order: 1
      });
    }
    setNewFeature('');
  }, [data, mode, open]);

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const tierColors = {
    basic: '#6B7280',
    silver: '#9CA3AF',
    gold: '#F59E0B',
    platinum: '#8B5CF6'
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Subscription Tier' : 'Tambah Subscription Tier'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit' ? 'Perbarui detail subscription tier' : 'Buat subscription tier baru untuk pengguna'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tier-name">Tier Name</Label>
              <Select
                value={formData.name}
                onValueChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  name: value,
                  display_name: prev.display_name || value.charAt(0).toUpperCase() + value.slice(1),
                  color: tierColors[value] || prev.color
                }))}
              >
                <SelectTrigger id="tier-name" data-testid="tier-name-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tier-display-name">Display Name</Label>
              <Input
                id="tier-display-name"
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="Nama yang ditampilkan"
                required
                data-testid="tier-display-name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tier-description">Deskripsi</Label>
            <Textarea
              id="tier-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Deskripsi singkat tier ini"
              rows={2}
              data-testid="tier-description"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tier-price">Harga (Rp)</Label>
              <Input
                id="tier-price"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                min={0}
                data-testid="tier-price"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tier-duration">Durasi (Hari)</Label>
              <Input
                id="tier-duration"
                type="number"
                value={formData.duration_days}
                onChange={(e) => setFormData(prev => ({ ...prev, duration_days: parseInt(e.target.value) || 30 }))}
                min={1}
                data-testid="tier-duration"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tier-max-tryouts">Max Try Out/Bulan</Label>
              <Input
                id="tier-max-tryouts"
                type="number"
                value={formData.max_tryouts_per_month}
                onChange={(e) => setFormData(prev => ({ ...prev, max_tryouts_per_month: parseInt(e.target.value) || 0 }))}
                min={-1}
                placeholder="-1 = Unlimited"
                data-testid="tier-max-tryouts"
              />
              <p className="text-xs text-slate-500">-1 untuk unlimited</p>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-2">
            <Label>Fitur</Label>
            <div className="flex gap-2">
              <Input
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Tambah fitur baru"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                data-testid="tier-new-feature"
              />
              <Button type="button" onClick={handleAddFeature} variant="outline" data-testid="add-feature-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.features.map((feature, i) => (
                <Badge key={i} variant="secondary" className="flex items-center gap-1 py-1">
                  {feature}
                  <button type="button" onClick={() => handleRemoveFeature(i)} className="ml-1 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Additional Options */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="tier-zoom">Termasuk Zoom</Label>
                <Switch
                  id="tier-zoom"
                  checked={formData.includes_zoom}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, includes_zoom: checked }))}
                  data-testid="tier-zoom-switch"
                />
              </div>
              {formData.includes_zoom && (
                <div className="space-y-2">
                  <Label htmlFor="tier-zoom-sessions">Sesi Zoom/Bulan</Label>
                  <Input
                    id="tier-zoom-sessions"
                    type="number"
                    value={formData.zoom_sessions_per_month}
                    onChange={(e) => setFormData(prev => ({ ...prev, zoom_sessions_per_month: parseInt(e.target.value) || 0 }))}
                    min={0}
                    data-testid="tier-zoom-sessions"
                  />
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="tier-ebook">Termasuk E-Book</Label>
                <Switch
                  id="tier-ebook"
                  checked={formData.includes_ebook}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, includes_ebook: checked }))}
                  data-testid="tier-ebook-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="tier-wag">Termasuk WAG</Label>
                <Switch
                  id="tier-wag"
                  checked={formData.includes_wag}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, includes_wag: checked }))}
                  data-testid="tier-wag-switch"
                />
              </div>
              {formData.includes_wag && (
                <div className="space-y-2">
                  <Label htmlFor="tier-wag-type">Tipe WAG</Label>
                  <Select
                    value={formData.wag_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, wag_type: value }))}
                  >
                    <SelectTrigger id="tier-wag-type" data-testid="tier-wag-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="vip">VIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tier-color">Warna</Label>
              <div className="flex gap-2">
                <Input
                  id="tier-color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                  data-testid="tier-color"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tier-sort">Urutan</Label>
              <Input
                id="tier-sort"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                min={1}
                data-testid="tier-sort"
              />
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-2">
                <Switch
                  id="tier-active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  data-testid="tier-active-switch"
                />
                <Label htmlFor="tier-active">Aktif</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={saving || !formData.display_name} data-testid="save-tier-btn">
              {saving ? 'Menyimpan...' : mode === 'edit' ? 'Simpan Perubahan' : 'Tambah Tier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// User Edit Dialog Component
function UserEditDialog({ open, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    role: 'student',
    password: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        school: data.school || '',
        role: data.role || 'student',
        password: ''
      });
    }
  }, [data, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only send password if it's filled
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await onSave(updateData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base">Edit User</DialogTitle>
          <DialogDescription className="text-xs">Perbarui informasi pengguna</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="user-name" className="text-xs">Nama <span className="text-red-500">*</span></Label>
            <Input
              id="user-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nama lengkap"
              required
              className="h-8 text-sm"
              data-testid="edit-user-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="user-email" className="text-xs">Email <span className="text-red-500">*</span></Label>
              <Input
                id="user-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
                required
                className="h-8 text-sm"
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-phone" className="text-xs">No. Telepon</Label>
              <Input
                id="user-phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="08xxxxxxxxxx"
                className="h-8 text-sm"
                data-testid="edit-user-phone"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="user-school" className="text-xs">Sekolah/Instansi</Label>
              <Input
                id="user-school"
                value={formData.school}
                onChange={(e) => setFormData(prev => ({ ...prev, school: e.target.value }))}
                placeholder="Nama sekolah"
                className="h-8 text-sm"
                data-testid="edit-user-school"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="user-role" className="text-xs">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger id="user-role" className="h-8 text-sm" data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="user-password" className="text-xs">Password Baru (opsional)</Label>
            <Input
              id="user-password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Kosongkan jika tidak ingin mengubah"
              className="h-8 text-sm"
              data-testid="edit-user-password"
            />
          </div>
          <DialogFooter className="pt-2 gap-2">
            <Button type="button" variant="outline" size="sm" className="h-8" onClick={onClose}>Batal</Button>
            <Button type="submit" size="sm" className="h-8" disabled={saving || !formData.name || !formData.email} data-testid="save-user-btn">
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

